<script setup lang="ts">
import { ref, onMounted, computed, watch, nextTick, onBeforeUnmount } from 'vue'
import { useI18n } from '~/composables/useI18n'
import {
    canShowGenerateSimilar,
    getInspirationType,
    formatPath,
    getInspirationOutputType,
} from '~/utils/inspiration'
import { useUserStore } from '@/stores/user'
import { emitOpenGoodsModal, emitter } from '~/utils/emitter'
import {
    noticeMessagePopup,
    isVideoOrImg,
    getResourceThumbnail,
} from '~/utils/common'
import { useMusicPlayerStore } from '~/stores/musicPlayer'
import { getCreationShareLinkApi } from '~/api/creations'
import { getInspirationDetailApi } from '~/api/inspiration'
import { analyticsEvent } from '~/utils/log-event-func'
import {
    buildWorkbenchPrefillFromInspirationItem,
    isInChatPageRuntime,
    writeWorkbenchInspirationPrefillToStorage,
} from '~/utils/workbenchInspirationPrefill'
import {
    buildAlmightyMentionAssets,
    getAlmightyMentionLabel,
    localizeAlmightyProtocolPrompt,
    normalizeAlmightyPromptMentionTokens,
    serializeAlmightyPromptMentionTokens,
    type AlmightyPromptMentionToken,
} from '~/utils/almightyMention'
import {
    getAlmightyHistoryReferenceItems,
    isAlmightyReferenceHistoryRecord,
    isHailuo03AlmightyHistoryRecord,
} from '~/utils/almightyHistory'
import workbenchLocaleMessages from '~/locales/modules/workbench'


const { t, locale } = useI18n()
const userStore = useUserStore()
const musicPlayer = useMusicPlayerStore()
const getInspirationId = (item: any) => item?.ins_id || item?.id

// 判断是否为音乐类型
const isMusicType = computed(
    () => props.showDataItemInfo?.type === 'MUSIC_CREATION_GENERATION'
)

const canGenerateSimilar = computed(() =>
    canShowGenerateSimilar(props.showDataItemInfo?.type as string | undefined)
)

const is_characters_type = computed(() => {
    return props.showDataItemInfo?.param?.characters?.length > 0
})

// 当前音乐 ID
const currentMusicId = computed(() => getInspirationId(props.showDataItemInfo))
// 参考音频的 ID（用于区分）
const refAudioId = computed(() => `ref_${currentMusicId.value}`)
const ALMIGHTY_AUDIO_ICON_URL =
    'https://static.weryai.com/growth/image/weryai_e1aa3c3b8d575035485d6e8ae90da6b9.png'
const almightyPreviewDurationMap = ref<Record<string, number>>({})
const almightyPreviewDurationLoadingSet = new Set<string>()

type AlmightyReferenceKind = 'image' | 'video' | 'audio'
type AlmightyReferenceItem = {
    id: string
    kind: AlmightyReferenceKind
    url: string
    label: string
    duration?: number
    posterUrl: string
}

type AlmightyPromptSegment =
    | {
        type: 'text'
        key: string
        text: string
    }
    | {
        type: 'mention'
        key: string
        label: string
        item: AlmightyReferenceItem
    }

// 进度条元素引用
const progressBarRef = ref<HTMLDivElement>()

// 从 Store 获取播放状态
const isPlaying = computed(() =>
    musicPlayer.isCurrentPlaying(currentMusicId.value)
)
const isLoading = computed(() =>
    musicPlayer.isCurrentLoading(currentMusicId.value)
)
const currentTime = computed(() => {
    if (musicPlayer.currentId === currentMusicId.value) {
        return musicPlayer.currentTime
    }
    return 0
})
const duration = computed(() => {
    if (musicPlayer.currentId === currentMusicId.value) {
        return musicPlayer.duration
    }
    return 0
})

// 进度百分比
const progressPercent = computed(() => {
    if (musicPlayer.currentId === currentMusicId.value) {
        return musicPlayer.progressPercent
    }
    return 0
})

// 格式化时间
const formatTime = (seconds: number): string => {
    return musicPlayer.formatTime(seconds)
}

// 音乐封面图
const musicCoverImage = computed(() => props.showDataItemInfo?.output || '')

// 音频URL
const musicAudioUrl = computed(() => props.showDataItemInfo?.output_audio || '')

// 音乐标题
const musicTitle = computed(
    () =>
        props.showDataItemInfo?.music?.music_title ||
        t('inspiration.untitledMusic')
)

// 音乐风格标签
const musicStyleTags = computed(() => {
    const styles = props.showDataItemInfo?.param?.styles
    return Array.isArray(styles) ? styles : []
})

// 歌词
const musicLyrics = computed(
    () => props.showDataItemInfo?.music?.generated_lyrics || ''
)

// 判断音乐数据是否已加载完成（用于区分"正在加载"和"确实没有歌词"）
const isMusicDataLoaded = computed(
    () => !!props.showDataItemInfo?.music
)

// 参考音频 URL（从 param.audio_urls 数组取第一条）
const referenceAudioUrl = computed(
    () => props.showDataItemInfo?.param?.audio_urls?.[0] || ''
)

// 参考音频名称（从 param.name 字段获取）
const referenceAudioName = computed(() => {
    return (
        props.showDataItemInfo?.param?.name || t('inspiration.referenceAudio')
    )
})

const isAlmightyReference = computed(
    () => isAlmightyReferenceHistoryRecord(props.showDataItemInfo)
)

const normalizeAlmightyMediaDuration = (value: unknown) => {
    const duration = Number(value)
    return Number.isFinite(duration) && duration > 0 ? duration : undefined
}

const getAlmightyMediaPoster = (value: any, url: string) => {
    if (typeof value?.thumbnail === 'string' && value.thumbnail) return value.thumbnail
    if (typeof value?.thumbnail_url === 'string' && value.thumbnail_url) return value.thumbnail_url
    if (typeof value?.cover === 'string' && value.cover) return value.cover
    if (typeof value?.cover_url === 'string' && value.cover_url) return value.cover_url
    return getResourceThumbnail(url)
}

const buildAlmightyFallbackLabel = (
    kind: AlmightyReferenceKind,
    index: number
) => `${t(`workbench.uploadLabels.${kind}`)}${index + 1}`

const almightyReferenceItems = computed<AlmightyReferenceItem[]>(() => {
    const counters: Record<AlmightyReferenceKind, number> = {
        image: 0,
        video: 0,
        audio: 0,
    }
    return getAlmightyHistoryReferenceItems(props.showDataItemInfo).map(item => {
        counters[item.kind] += 1
        const url = item.url || ''
        return {
            id: item.id,
            kind: item.kind,
            url,
            label: item.name || buildAlmightyFallbackLabel(item.kind, counters[item.kind] - 1),
            duration: normalizeAlmightyMediaDuration(item.duration),
            posterUrl: item.kind === 'video'
                ? getAlmightyMediaPoster(item, url)
                : item.kind === 'audio'
                    ? ALMIGHTY_AUDIO_ICON_URL
                    : url,
        }
    })
})

const almightyPromptTokens = computed<AlmightyPromptMentionToken[]>(() => {
    return normalizeAlmightyPromptMentionTokens(
        Array.isArray(props.showDataItemInfo?.param?.almighty_prompt_tokens)
            ? props.showDataItemInfo.param.almighty_prompt_tokens
            : [],
        props.showDataItemInfo?.param?.prompt || ''
    )
})

const isHailuo03Record = computed(() => {
    return isHailuo03AlmightyHistoryRecord(props.showDataItemInfo)
})

const almightyMentionAssets = computed(() =>
    buildAlmightyMentionAssets(almightyReferenceItems.value, t)
)
const almightyProtocolMentionAssets = computed(() =>
    buildAlmightyMentionAssets(almightyReferenceItems.value, t, true)
)

const ALMIGHTY_PROMPT_EN_LABELS: Record<AlmightyReferenceKind, string> = {
    image: 'Image',
    video: 'Video',
    audio: 'Audio',
}

const ALMIGHTY_PROMPT_EXTRA_LABELS: Record<AlmightyReferenceKind, string[]> = {
    image: ['图像', 'img', 'photo'],
    video: ['vid'],
    audio: ['音乐', 'music', 'sound'],
}

const escapeRegex = (value: string) =>
    value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const buildEnglishMentionLabel = (
    kind: AlmightyReferenceKind,
    index: number
) => `${ALMIGHTY_PROMPT_EN_LABELS[kind]}${index}`

const allLocalePromptMentionKeywords = computed<Record<AlmightyReferenceKind, string[]>>(() => {
    const result: Record<AlmightyReferenceKind, Set<string>> = {
        image: new Set(ALMIGHTY_PROMPT_EXTRA_LABELS.image),
        video: new Set(ALMIGHTY_PROMPT_EXTRA_LABELS.video),
        audio: new Set(ALMIGHTY_PROMPT_EXTRA_LABELS.audio),
    }

    ;(['image', 'video', 'audio'] as AlmightyReferenceKind[]).forEach(kind => {
        Object.values(workbenchLocaleMessages || {}).forEach((localeItem: any) => {
            const translated = localeItem?.uploadLabels?.[kind]
            if (typeof translated === 'string' && translated.trim()) {
                result[kind].add(translated.trim())
            }
        })
    })

    return {
        image: Array.from(result.image),
        video: Array.from(result.video),
        audio: Array.from(result.audio),
    }
})

const allPromptMentionKeywordMap = computed(() => {
    const map = new Map<string, AlmightyReferenceKind>()
    ;(['image', 'video', 'audio'] as AlmightyReferenceKind[]).forEach(kind => {
        allLocalePromptMentionKeywords.value[kind].forEach(label => {
            const normalized = String(label || '').trim().toLowerCase()
            if (normalized) {
                map.set(normalized, kind)
            }
        })
    })
    return map
})

const almightyPromptReferenceEntries = computed(() => {
    const counters: Record<AlmightyReferenceKind, number> = {
        image: 0,
        video: 0,
        audio: 0,
    }

    return almightyReferenceItems.value.map((item, index) => {
        counters[item.kind] += 1
        const kindIndex = counters[item.kind]
        const asset = almightyMentionAssets.value[index]
        return {
            item,
            assetId: asset?.id || item.id,
            kindIndex,
            label:
                asset?.label ||
                getAlmightyMentionLabel(item.kind, kindIndex, t),
            aliases: Array.from(
                new Set(
                    [
                        asset?.label,
                        getAlmightyMentionLabel(item.kind, kindIndex, t),
                        getAlmightyMentionLabel(item.kind, kindIndex),
                        buildEnglishMentionLabel(item.kind, kindIndex),
                        ...allLocalePromptMentionKeywords.value[item.kind].map(
                            keyword => `${keyword}${kindIndex}`
                        ),
                    ].filter(Boolean)
                )
            ) as string[],
        }
    })
})

const almightyPromptEntryByAssetId = computed(() => {
    return new Map(
        almightyPromptReferenceEntries.value.map(entry => [entry.assetId, entry])
    )
})

const almightyPromptEntriesByKind = computed(() => {
    const grouped: Record<AlmightyReferenceKind, typeof almightyPromptReferenceEntries.value> = {
        image: [],
        video: [],
        audio: [],
    }

    almightyPromptReferenceEntries.value.forEach(entry => {
        grouped[entry.item.kind].push(entry)
    })

    return grouped
})

const getPromptMentionKindFromKeyword = (
    keyword: string
): AlmightyReferenceKind | null => {
    const normalized = String(keyword || '').trim().toLowerCase()
    return allPromptMentionKeywordMap.value.get(normalized) || null
}

const getPromptMentionIndex = (label: string) => {
    const match = String(label || '').match(/(\d+)\s*$/)
    const index = Number(match?.[1] || 0)
    return Number.isInteger(index) && index > 0 ? index : null
}

const resolvePromptMentionEntry = (token: Extract<AlmightyPromptMentionToken, { type: 'mention' }>) => {
    const byAssetId = almightyPromptEntryByAssetId.value.get(token.assetId)
    if (byAssetId) return byAssetId

    const fallbackLabel = String(token.fallbackLabel || '').trim()
    if (fallbackLabel) {
        const byAlias = almightyPromptReferenceEntries.value.find(entry =>
            entry.aliases.some(alias => alias.toLowerCase() === fallbackLabel.toLowerCase())
        )
        if (byAlias) return byAlias
    }

    const mentionIndex = getPromptMentionIndex(fallbackLabel)
    if (!mentionIndex) return null

    const entries = almightyPromptEntriesByKind.value[token.kind] || []
    return entries[mentionIndex - 1] || null
}

const buildPromptSegmentsFromRawPrompt = (promptText: string): AlmightyPromptSegment[] => {
    if (!promptText) return []

    const segments: AlmightyPromptSegment[] = []
    const mentionKeywords = Array.from(allPromptMentionKeywordMap.value.keys()).sort(
        (a, b) => b.length - a.length
    )
    if (mentionKeywords.length === 0) {
        return [
            {
                type: 'text',
                key: 'text-plain',
                text: promptText,
            },
        ]
    }

    const mentionRegex = new RegExp(
        `(${mentionKeywords.map(escapeRegex).join('|')})\\s*(\\d+)`,
        'gi'
    )
    let lastIndex = 0
    let match: RegExpExecArray | null = null

    while ((match = mentionRegex.exec(promptText)) !== null) {
        const rawLabel = match[0] || ''
        const keyword = match[1] || ''
        const indexText = match[2] || ''
        const mentionKind = getPromptMentionKindFromKeyword(keyword)
        const mentionIndex = Number(indexText || 0)

        if (!mentionKind || !Number.isInteger(mentionIndex) || mentionIndex <= 0) {
            continue
        }

        const matchedEntry = almightyPromptEntriesByKind.value[mentionKind]?.[mentionIndex - 1]
        if (!matchedEntry) {
            continue
        }

        if (match.index > lastIndex) {
            segments.push({
                type: 'text',
                key: `text-${lastIndex}-${match.index}`,
                text: promptText.slice(lastIndex, match.index),
            })
        }

        segments.push({
            type: 'mention',
            key: `mention-${matchedEntry.assetId}-${match.index}`,
            label: matchedEntry.label,
            item: matchedEntry.item,
        })

        lastIndex = match.index + rawLabel.length
    }

    if (lastIndex < promptText.length) {
        segments.push({
            type: 'text',
            key: `text-${lastIndex}-${promptText.length}`,
            text: promptText.slice(lastIndex),
        })
    }

    return segments.length > 0
        ? segments
        : [
            {
                type: 'text',
                key: 'text-plain',
                text: promptText,
            },
        ]
}

const almightyPromptSegments = computed<AlmightyPromptSegment[]>(() => {
    if (!isAlmightyReference.value) return []

    const rawPrompt = props.showDataItemInfo?.param?.prompt || ''
    const promptText = isHailuo03Record.value
        ? localizeAlmightyProtocolPrompt(rawPrompt, t, almightyProtocolMentionAssets.value)
        : rawPrompt
    const hasMentionToken = almightyPromptTokens.value.some(
        token => token.type === 'mention'
    )

    if (!hasMentionToken) {
        return buildPromptSegmentsFromRawPrompt(promptText)
    }

    return almightyPromptTokens.value.map((token, index) => {
        if (token.type === 'text') {
            return {
                type: 'text' as const,
                key: `text-${index}`,
                text: token.text,
            }
        }

        const matchedEntry = resolvePromptMentionEntry(token)
        if (!matchedEntry) {
            return {
                type: 'text' as const,
                key: `mention-fallback-${index}`,
                text: token.fallbackLabel,
            }
        }

        return {
            type: 'mention' as const,
            key: `mention-${matchedEntry.assetId}-${index}`,
            label: matchedEntry.label,
            item: matchedEntry.item,
        }
    })
})

const hasRichAlmightyPromptSegments = computed(() =>
    almightyPromptSegments.value.some(segment => segment.type === 'mention')
)

const promptCopyText = computed(() => {
    const rawPrompt = props.showDataItemInfo?.param?.prompt || ''
    if (!isAlmightyReference.value) return rawPrompt

    if (almightyPromptTokens.value.some(token => token.type === 'mention')) {
        return serializeAlmightyPromptMentionTokens(
            almightyPromptTokens.value,
            almightyMentionAssets.value
        )
    }

    return isHailuo03Record.value
        ? localizeAlmightyProtocolPrompt(rawPrompt, t, almightyProtocolMentionAssets.value)
        : rawPrompt
})

const handlePromptMentionClick = (previewItem: AlmightyReferenceItem) => {
    if (previewItem.kind === 'audio') {
        toggleAlmightyAudioPreview(previewItem)
        return
    }
    openAlmightyReferencePreview(previewItem)
}

const getAlmightyDurationText = (duration?: number) => {
    if (!duration) return ''
    const safeSeconds = Math.max(0, Math.ceil(Number(duration)))
    const minutes = `${Math.floor(safeSeconds / 60)}`.padStart(2, '0')
    const seconds = `${safeSeconds % 60}`.padStart(2, '0')
    return `${minutes}:${seconds}`
}

const loadAlmightyPreviewDuration = async (
    previewItem: Pick<AlmightyReferenceItem, 'id' | 'kind' | 'url'>
) => {
    if (!import.meta.client) return
    if (!previewItem.url) return
    if (previewItem.kind !== 'video' && previewItem.kind !== 'audio') return
    if (almightyPreviewDurationMap.value[previewItem.id]) return
    if (almightyPreviewDurationLoadingSet.has(previewItem.id)) return

    almightyPreviewDurationLoadingSet.add(previewItem.id)

    try {
        const media = document.createElement(
            previewItem.kind === 'audio' ? 'audio' : 'video'
        )
        media.preload = 'metadata'
        media.muted = true
        if (previewItem.kind === 'video') {
            ;(media as HTMLVideoElement).playsInline = true
        }

        const duration = await new Promise<number>((resolve, reject) => {
            const timeout = window.setTimeout(() => {
                cleanup()
                reject(new Error('almighty-detail-duration-timeout'))
            }, 10000)

            const cleanup = () => {
                window.clearTimeout(timeout)
                media.onloadedmetadata = null
                media.onerror = null
                try {
                    media.pause()
                    media.removeAttribute('src')
                    media.load()
                } catch {
                    // ignore
                }
            }

            media.onloadedmetadata = () => {
                const nextDuration = Number(media.duration || 0)
                cleanup()
                if (Number.isFinite(nextDuration) && nextDuration > 0) {
                    resolve(nextDuration)
                    return
                }
                reject(new Error('almighty-detail-duration-invalid'))
            }

            media.onerror = () => {
                cleanup()
                reject(new Error('almighty-detail-duration-error'))
            }

            media.src = previewItem.url
        })

        almightyPreviewDurationMap.value = {
            ...almightyPreviewDurationMap.value,
            [previewItem.id]: duration,
        }
    } catch {
        // 详情页卡片时长读取失败时静默处理，不影响主流程
    } finally {
        almightyPreviewDurationLoadingSet.delete(previewItem.id)
    }
}

const getResolvedAlmightyDuration = (previewItem: AlmightyReferenceItem) => {
    const duration =
        previewItem.duration || almightyPreviewDurationMap.value[previewItem.id]

    if (!duration && (previewItem.kind === 'video' || previewItem.kind === 'audio')) {
        void loadAlmightyPreviewDuration(previewItem)
    }

    return duration
}

const openAlmightyReferencePreview = (previewItem: AlmightyReferenceItem) => {
    if (!previewItem.url) return
    if (previewItem.kind === 'image') {
        emitter.emit('open-image-preview', previewItem.url)
        return
    }
    if (previewItem.kind === 'video') {
        emitter.emit('open-video-preview', previewItem.url)
    }
}

const toggleAlmightyAudioPreview = (previewItem: AlmightyReferenceItem) => {
    if (previewItem.kind !== 'audio' || !previewItem.url) {
        noticeMessagePopup(t('inspiration.noAudio'), '', 'warning')
        return
    }

    musicPlayer.play(previewItem.id, previewItem.url)
}

const isAlmightyAudioPlaying = (previewItem: AlmightyReferenceItem) => {
    return previewItem.kind === 'audio' && musicPlayer.isCurrentPlaying(previewItem.id)
}

const getAlmightyAudioCurrentTimeText = (previewItem: AlmightyReferenceItem) => {
    if (previewItem.kind !== 'audio') return '00:00'
    if (musicPlayer.currentId !== previewItem.id) return '00:00'
    return getAlmightyDurationText(musicPlayer.currentTime) || '00:00'
}

const stopAlmightyReferenceAudio = () => {
    const currentId = String(musicPlayer.currentId || '')
    if (!currentId.startsWith('almighty-detail-')) return
    musicPlayer.stop()
}

// 参考音频播放状态（从 Store 获取）
const isRefAudioPlaying = computed(() =>
    musicPlayer.isCurrentPlaying(refAudioId.value)
)
const isRefAudioLoading = computed(() =>
    musicPlayer.isCurrentLoading(refAudioId.value)
)

// 播放/暂停参考音频
const toggleRefAudioPlay = () => {
    if (!referenceAudioUrl.value) {
        noticeMessagePopup(t('inspiration.noAudio'), '', 'warning')
        return
    }

    // 使用全局播放器播放参考音频（使用不同的 ID 区分）
    musicPlayer.play(refAudioId.value, referenceAudioUrl.value)
}

// 下载参考音频
const downloadRefAudio = async () => {
    if (!referenceAudioUrl.value) {
        noticeMessagePopup(t('inspiration.noAudio'), '', 'warning')
        return
    }

    const filename = `weryai_ref_audio_${Date.now()}.mp3`
    downloadMedia(referenceAudioUrl.value, filename)
}

// 播放/暂停
const toggleMusicPlay = () => {
    if (!musicAudioUrl.value) {
        noticeMessagePopup(t('inspiration.noAudio'), '', 'warning')
        return
    }

    // 使用全局播放器播放
    musicPlayer.play(currentMusicId.value, musicAudioUrl.value)
}

// 进度条点击
const handleAudioSeek = (e: MouseEvent) => {
    if (!progressBarRef.value) return
    if (musicPlayer.currentId !== currentMusicId.value) return

    const rect = progressBarRef.value.getBoundingClientRect()
    const percent = ((e.clientX - rect.left) / rect.width) * 100
    musicPlayer.seekByPercent(percent)
}

// 回退5秒
const seekBackward = () => {
    if (musicPlayer.currentId !== currentMusicId.value) return
    musicPlayer.seek(Math.max(0, musicPlayer.currentTime - 5))
}

// 前进5秒
const seekForward = () => {
    if (musicPlayer.currentId !== currentMusicId.value) return
    musicPlayer.seek(
        Math.min(musicPlayer.duration, musicPlayer.currentTime + 5)
    )
}

// 上报特效详情页事件
const sendEffectsDetailsEvent = (slug: string, from: string = 'create') => {
    if (!import.meta.client) return
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
    const local_channel = localStorage.getItem('channel') || 'official'
    analyticsEvent('click_linggan_card', {
        effect_id: slug,
        from,
        uid: userInfo?.uid,
        channel: local_channel,
    })
}

// 音乐做同款
const handleMusicMakeSimilar = () => {
    const item = props.showDataItemInfo
    if (!item) return
    if (!import.meta.client) return

    // Also prefill PcWorkbench, but keep its current compact/expand UI state unchanged.
    const prefill = buildWorkbenchPrefillFromInspirationItem(item)
    if (prefill) {
        writeWorkbenchInspirationPrefillToStorage(prefill)
        emitter.emit(
            'workbench-apply-prefill',
            isInChatPageRuntime()
                ? prefill
                : { prefill, preserveWorkbenchState: true }
        )
    }

    // 通过事件总线发送数据，填充表单（同页面时使用）
    emitter.emit('fill-music-form', item)

    const currentPath = window.location.pathname
    // 如果已在 text-to-music 页面，只需关闭弹窗（事件总线已填充表单）
    if (currentPath.includes('text-to-music')) {
        handleCloseMask()
        return
    }

    // 跳转到音乐生成页面（与图片/视频做同款保持一致）
    const origin = window.location.origin
    const timestamp = Date.now()
    const path = `${origin}/${locale.value}/text-to-music?customType=inspiration&t=${timestamp}`

    localStorage.setItem('inspirationData', JSON.stringify(item))
    const inspirationId = getInspirationId(item)
    if (inspirationId) {
        localStorage.setItem('inspirationInsId', String(inspirationId))
    }
    sendEffectsDetailsEvent(item?.id, 'url')

    handleCloseMask()
    location.href = path
}

// 复制歌词
const copyLyrics = async () => {
    if (!musicLyrics.value) {
        noticeMessagePopup(t('inspiration.noLyrics'), '', 'warning')
        return
    }
    try {
        await navigator.clipboard.writeText(musicLyrics.value)
        noticeMessagePopup(t('inspiration.copySuccess'), '', 'success', 3000)
    } catch (err) {
        console.error('复制失败:', err)
        noticeMessagePopup(t('inspiration.copyFailed'), '', 'error', 3000)
    }
}

// 下载音乐
const downloadMusic = async () => {
    if (!musicAudioUrl.value) {
        noticeMessagePopup(t('inspiration.noAudio'), '', 'warning')
        return
    }

    const inspirationId = getInspirationId(props.showDataItemInfo)
    const filename = `weryai_music_${inspirationId}.mp3`
    downloadMedia(musicAudioUrl.value, filename)
}

// 判断用户是否为 VIP 会员
const isVipUser = computed(() => {
    return !!userStore.accountSummary?.sub_info?.subscription_type
})

// 图片生成类型
const generate_type_list = computed(() => [
    {
        label: t('inspiration.imageToVideo'),
        value: 'image-to-video',
    },
    {
        label: t('inspiration.imageToImage'),
        value: 'image-to-image',
    },
])

// 语言代码到浏览器 locale 的映射
const localeMap: Record<string, string> = {
    zh: 'zh-CN',
    en: 'en-US',
    ja: 'ja-JP',
    ko: 'ko-KR',
    es: 'es-ES',
    fr: 'fr-FR',
    de: 'de-DE',
    ru: 'ru-RU',
    pt: 'pt-PT',
    it: 'it-IT',
    ar: 'ar-SA',
}

const props = defineProps({
    // 展示的数据信息
    showDataItemInfo: {
        type: Object,
        default: () => ({}),
    },
    hidden_like: {
        type: Boolean,
        default: false,
    },
    // 是否需要通过 API 获取分享链接（my-creations 页面需要）
    need_api_share_link: {
        type: Boolean,
        default: false,
    },
})

// 添加响应式状态来存储实际尺寸
const actualSize = ref<{ width: number; height: number } | null>(null)
const isLoadingSize = ref(false)
const mediaLoading = ref(true) // 用于控制主媒体的加载状态
const mainMediaRef = ref<HTMLImageElement | HTMLVideoElement | null>(null)
let ensureLoadedTimer: ReturnType<typeof setTimeout> | null = null

const clearEnsureLoadedTimer = () => {
    if (ensureLoadedTimer) {
        clearTimeout(ensureLoadedTimer)
        ensureLoadedTimer = null
    }
}

/**
 * 兜底：在 SSR/水合或命中缓存的情况下，@load/@loadeddata 可能不会触发，
 * 但媒体其实已经处于已加载状态。这里通过 DOM 状态判断并关闭 loading。
 */
const ensureMediaLoadedState = () => {
    const el = mainMediaRef.value
    if (!el) return

    // 图片：complete + naturalWidth > 0 才算真正加载成功
    if (el instanceof HTMLImageElement) {
        if (el.complete && el.naturalWidth > 0) {
            mediaLoading.value = false
        }
        return
    }

    // 视频：readyState >= 2 (HAVE_CURRENT_DATA) 说明已经有可播放的当前帧
    // 这里不强依赖 loadeddata 事件，避免事件丢失导致卡 loading
    const video = el as HTMLVideoElement
    if (video.readyState >= 2) {
        mediaLoading.value = false
    }
}

// 加载实际媒体尺寸的函数
const loadActualSize = () => {
    const url = props.showDataItemInfo?.output_with_watermark
    if (!url) return

    isLoadingSize.value = true
    actualSize.value = null // 重置之前的值

    // 判断是图片还是视频
    const isVideo =
        getInspirationType(props.showDataItemInfo.type) === 'VIDEO' ||
        getInspirationOutputType(url) === 'VIDEO'

    if (isVideo) {
        // 加载视频尺寸
        const video = document.createElement('video')
        video.preload = 'metadata' // 只加载元数据，不加载整个视频

        video.onloadedmetadata = () => {
            actualSize.value = {
                width: video.videoWidth,
                height: video.videoHeight,
            }
            isLoadingSize.value = false
        }

        video.onerror = () => {
            console.error('视频尺寸加载失败')
            isLoadingSize.value = false
        }

        video.src = url
    } else {
        // 加载图片尺寸
        const img = new Image()

        img.onload = () => {
            actualSize.value = {
                width: img.naturalWidth,
                height: img.naturalHeight,
            }
            isLoadingSize.value = false
        }

        img.onerror = () => {
            console.error('图片尺寸加载失败')
            isLoadingSize.value = false
        }

        img.src = url
    }
}

/**
 * 生成响应式图片的 srcset（用于主媒体图片）
 * @param imageUrl - 原始图片 URL
 * @returns srcset 字符串
 */
const getImageSrcset = (imageUrl: string): string => {
    if (!imageUrl) return ''

    // 检查 URL 是否已经包含参数
    const hasParams = imageUrl.includes('?')
    const separator = hasParams ? '&' : '?'

    // 使用宽度描述符（w）提供不同尺寸
    // 主媒体图片通常较大，提供更多尺寸选项
    return [
        `${imageUrl}${separator}w=640 640w`,   // 移动端小屏
        `${imageUrl}${separator}w=960 960w`,   // 移动端大屏/平板
        `${imageUrl}${separator}w=1280 1280w`, // 桌面端标准
        `${imageUrl}${separator}w=1920 1920w`, // 桌面端大屏
        `${imageUrl}${separator}w=2560 2560w`,  // 高DPI/Retina
    ].join(', ')
}

/**
 * 生成音乐封面图的 srcset（用于音乐封面，尺寸较小）
 * @param imageUrl - 原始图片 URL
 * @returns srcset 字符串
 */
const getMusicCoverSrcset = (imageUrl: string): string => {
    if (!imageUrl) return ''

    // 检查 URL 是否已经包含参数
    const hasParams = imageUrl.includes('?')
    const separator = hasParams ? '&' : '?'

    // 音乐封面图通常较小，提供适中的尺寸选项
    return [
        `${imageUrl}${separator}w=200 200w`,   // 小屏
        `${imageUrl}${separator}w=400 400w`,  // 标准屏
        `${imageUrl}${separator}w=800 800w`,   // 高DPI
    ].join(', ')
}

// 计算输出尺寸
const outputSize = computed(() => {
    const item = props.showDataItemInfo

    // 1. 使用 resolution 参数（补充 720p/2k/4k 等）
    const resolution = item?.param?.resolution
    if (resolution) {
        const key = String(resolution).toLowerCase()
        const resolutionMap: Record<string, string> = {
            '720p': '720 × 1280',
            '1080p': '1080 × 1920',
            '1k': '1080 × 1920',
            '2k': '1440 × 2560',
            '4k': '2160 × 3840',
        }
        return resolutionMap[key] || resolution
    }

    // 2. 优先使用实际加载的尺寸（最准确）
    if (actualSize.value) {
        return `${actualSize.value.width} × ${actualSize.value.height}`
    }

    // 3. 无数据则不返回，保持空字符串，便于模板隐藏
    return ''
})

const parseAspectRatio = (aspect?: string): number | null => {
    if (!aspect) return null
    const match = String(aspect)
        .trim()
        .match(/^(\d+(?:\.\d+)?)\s*[:/]\s*(\d+(?:\.\d+)?)$/)
    if (!match) return null
    const width = Number(match[1])
    const height = Number(match[2])
    if (
        !Number.isFinite(width) ||
        !Number.isFinite(height) ||
        width <= 0 ||
        height <= 0
    ) {
        return null
    }
    return width / height
}

const isMainMediaVideo = computed(() => {
    const item = props.showDataItemInfo
    return (
        getInspirationType(item?.type) === 'VIDEO' ||
        getInspirationOutputType(item?.output) === 'VIDEO'
    )
})

const mediaRenderMeta = computed(() => {
    const item: any = props.showDataItemInfo
    const widthFromData = Number(actualSize.value?.width || item?.width)
    const heightFromData = Number(actualSize.value?.height || item?.height)

    if (
        Number.isFinite(widthFromData) &&
        Number.isFinite(heightFromData) &&
        widthFromData > 0 &&
        heightFromData > 0
    ) {
        return {
            width: Math.round(widthFromData),
            height: Math.round(heightFromData),
            aspectRatio: `${widthFromData} / ${heightFromData}`,
        }
    }

    const ratioFromParam = parseAspectRatio(item?.param?.aspect_ratio)
    if (ratioFromParam) {
        const fallbackWidth = Math.max(1, Math.round(ratioFromParam * 1000))
        return {
            width: fallbackWidth,
            height: 1000,
            aspectRatio: `${fallbackWidth} / 1000`,
        }
    }

    if (isMainMediaVideo.value) {
        return { width: 1280, height: 720, aspectRatio: '16 / 9' }
    }

    return { width: 1024, height: 1024, aspectRatio: '1 / 1' }
})

const videoPosterUrl = computed(() => {
    const item: any = props.showDataItemInfo
    const paramPoster = item?.param?.pic_urls?.[0]
    if (typeof paramPoster === 'string' && paramPoster.trim()) return paramPoster

    const cover = item?.output_with_watermark
    if (
        typeof cover === 'string' &&
        cover.trim() &&
        getInspirationOutputType(cover) === 'IMAGE'
    ) {
        return cover
    }

    return ''
})

// 阻止滚动穿透
const preventScroll = (e: Event) => {
    const target = (e.target as HTMLElement | null) || null
    if (!target) return

    // 检查是否在可滚动元素内
    const mainElement = target.closest('main')
    const infoElement = target.closest('.info')
    const sectionScrollElement = target.closest('.section-content-scroll')
    const musicScrollElement = target.closest('.music-content-scroll')
    const lyricsScrollElement = target.closest('.lyrics-display-area')

    // 获取实际需要滚动的元素
    const scrollElement =
        lyricsScrollElement ||
        musicScrollElement ||
        sectionScrollElement ||
        infoElement ||
        mainElement

    // 如果是在可滚动区域内
    if (scrollElement) {
        // 触摸滚动时不做「边界阻止」的精细判断（否则容易把内部滚动也拦掉）
        // 交给 CSS overscroll-behavior 来避免滚动传递；这里主要处理桌面端 wheel。
        if (!(e instanceof WheelEvent)) return

        const scrollTop = scrollElement.scrollTop
        const scrollHeight = scrollElement.scrollHeight
        const height = scrollElement.clientHeight
        const delta = e.deltaY

        // 只在到达边界时阻止滚动穿透
        const isAtTop = scrollTop === 0 && delta < 0
        const isAtBottom = scrollTop + height >= scrollHeight && delta > 0

        if (isAtTop || isAtBottom) {
            e.preventDefault() // 阻止滚动传播到背景
        }
        return
    }

    // 其他区域直接阻止滚动
    e.preventDefault()
}

// 关闭遮罩层
const emit = defineEmits(['closeMask', 'changeLikeStateForItem'])

// 关闭弹窗（停止播放并 emit closeMask）
const handleCloseMask = () => {
    // 如果正在播放当前弹窗的音乐（主音频或参考音频），则停止
    if (
        musicPlayer.currentId === currentMusicId.value ||
        musicPlayer.currentId === refAudioId.value
    ) {
        musicPlayer.stop()
    }
    stopAlmightyReferenceAudio()
    emit('closeMask')
}

// 格式化日期
const formatDate = (dateString: string) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    const browserLocale = localeMap[locale.value] || 'en-US'
    return date.toLocaleDateString(browserLocale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    })
}

// 复制提示词
const copyPrompt = async () => {
    const promptText = promptCopyText.value
    if (promptText) {
        try {
            await navigator.clipboard.writeText(promptText)
            noticeMessagePopup(
                t('inspiration.copySuccess'),
                '',
                'success',
                3000
            )
        } catch (err) {
            console.error('复制失败:', err)
            noticeMessagePopup(t('inspiration.copyFailed'), '', 'error', 3000)
        }
    }
}

const is_client = import.meta.client
// 导航跳转
const navigationChange = async (item: any) => {
    const id = getInspirationId(item)
    let payload = item
    if (id) {
        try {
            const res = await getInspirationDetailApi(String(id))
            if (res?.status === 200 && res?.data) {
                payload = res.data
            }
        } catch (e) {
            console.error('做同款前灵感详情加载失败，使用列表数据:', e)
        }
    }

    // Chat page: do NOT navigate; only prefill PcWorkbench.
    if (isInChatPageRuntime()) {
        const prefill = buildWorkbenchPrefillFromInspirationItem(payload)
        if (prefill) {
            emitter.emit('workbench-apply-prefill', prefill)
        }
        handleCloseMask()
        return
    }

    const targetPath = formatPath(
        payload.type,
        payload.param?.template_id,
        payload.param?.template_path
    )
    if (targetPath === 'noo') {
        noticeMessagePopup(t('inspiration.operationFailed'), '', 'warning', 3000)
        handleCloseMask()
        return
    }

    // Tool pages: also prefill PcWorkbench before leaving.
    const prefill = buildWorkbenchPrefillFromInspirationItem(payload)
    if (prefill) {
        writeWorkbenchInspirationPrefillToStorage(prefill)
        emitter.emit('workbench-apply-prefill', prefill)
    }
    const origin = window.location.origin // "https://weryai.com"

    const timestamp = Date.now()
    const path = `${origin}/${locale.value}/${targetPath}?customType=inspiration&t=${timestamp}`

    const dataWriteBack = JSON.stringify(payload)
    if (!is_client) return
    localStorage.setItem('inspirationData', dataWriteBack)
    const inspirationId = getInspirationId(payload)
    if (inspirationId) {
        localStorage.setItem('inspirationInsId', String(inspirationId))
    }
    sendEffectsDetailsEvent(payload?.id, 'url')
    handleCloseMask()
    // navigateTo(path)
    location.href = path
}

// 跳转图片区生成东西
const imageToGenerate = (inspirationId: string, path: string) => {
    const item = props.showDataItemInfo
    const dataWriteBack = item ? JSON.stringify(item) : ''
    if (!is_client) return

    localStorage.setItem('inspirationData', dataWriteBack)
    const inspirationIdFromItem = getInspirationId(item)
    if (inspirationIdFromItem) {
        localStorage.setItem('inspirationInsId', String(inspirationIdFromItem))
    }

    handleCloseMask()
    const origin = window.location.origin // "https://weryai.com"
    const timestamp = Date.now()
    const navPath = `${origin}/${locale.value}/${path}?imgId=${inspirationId}&t=${timestamp}`
    // navigateTo(path)
    location.href = navPath
}

// 在组件挂载时加载尺寸
onMounted(() => {
    loadActualSize()
    // 首次挂载也做一次兜底检查（避免水合后事件错过）
    nextTick(() => {
        ensureMediaLoadedState()
    })
})

// 监听输出地址变化，重置加载态，并兜底检测“已加载但事件没触发”的情况
watch(
    () => props.showDataItemInfo?.output,
    async val => {
        clearEnsureLoadedTimer()
        mediaLoading.value = !!val
        await nextTick()

        // 立即检查一次（常见：命中缓存，图片瞬间完成）
        ensureMediaLoadedState()

        // 再延迟检查一次（极快加载/水合时序问题）
        ensureLoadedTimer = setTimeout(() => {
            ensureMediaLoadedState()
        }, 200)
    },
    { immediate: true }
)

// 图片 / 视频加载完成后关闭 loading
const handleMediaLoaded = () => {
    clearEnsureLoadedTimer()
    mediaLoading.value = false
}

// 图片 / 视频加载失败也需要关闭 loading，避免永远卡住
const handleMediaError = (e?: Event) => {
    console.error('媒体加载失败，关闭 loading', e)
    clearEnsureLoadedTimer()
    mediaLoading.value = false
}

onBeforeUnmount(() => {
    clearEnsureLoadedTimer()
    stopAlmightyReferenceAudio()
})

/**
 * 生成 SEO 友好的 alt 文本
 * 格式: "AI generated {type} using {model}: {prompt_snippet}"
 * 例如: "AI generated image using FLUX: A beautiful sunset over the ocean..."
 */
const generateAltText = computed(() => {
    const item = props.showDataItemInfo
    if (!item) return 'AI generated content'

    const isVideo =
        getInspirationType(item.type) === 'VIDEO' ||
        getInspirationOutputType(item.output) === 'VIDEO'
    const contentType = isVideo ? 'video' : 'image'
    const model = item.param?.model || 'AI'
    const prompt = item.param?.prompt || ''

    // 截取 prompt 前 80 个字符作为摘要
    const promptSnippet =
        prompt.length > 80 ? prompt.slice(0, 80) + '...' : prompt

    if (promptSnippet) {
        return `AI generated ${contentType} using ${model}: ${promptSnippet}`
    }
    return `AI generated ${contentType} using ${model}`
})

/**
 * 生成参考图片的 alt 文本
 */
const generateRefImageAlt = (index: number | string) => {
    const item = props.showDataItemInfo
    const model = item?.param?.model || 'AI'
    const idx = typeof index === 'string' ? Number(index) : index
    const safeIndex = Number.isFinite(idx) ? idx : 0
    return `Reference image ${safeIndex + 1} for ${model} generation`
}

/**
 * 生成用户头像的 alt 文本
 */
const userAvatarAlt = computed(() => {
    const userName = props.showDataItemInfo?.user?.user_name
    return userName ? `${userName}'s avatar` : 'User avatar'
})

const copyText = async (text: string) => {
    try {
        await navigator.clipboard.writeText(text)
        // 成功提示
        noticeMessagePopup(t('inspiration.copySuccess'), '', 'success', 3000)
    } catch (err) {
        console.error('复制失败:', err)
        noticeMessagePopup(t('inspiration.copyFailed'), '', 'error', 3000)
    }
}

const isClient = import.meta.client

// 移动端：下载菜单不支持 hover，这里提供点击打开/关闭
const isDownloadMenuOpen = ref(false)
const toggleDownloadMenu = () => {
    isDownloadMenuOpen.value = !isDownloadMenuOpen.value
}
const closeDownloadMenu = (): void => {
    isDownloadMenuOpen.value = false
}

// 复制链接加载状态
const copy_link_loading = ref(false)

// 复制链接
const copyLink = async () => {
    if (!isClient) return
    if (copy_link_loading.value) return

    const item = props.showDataItemInfo
    const type = item?.type === 'MUSIC_CREATION_GENERATION' ? 'MUSIC' : (isVideoOrImg(item?.output) as string)
    const origin = window.location.origin

    // 如果需要通过 API 获取分享链接（my-creations 页面）
    if (props.need_api_share_link) {
        copy_link_loading.value = true
        try {
            // 获取 history_id 和 resource_url
            const history_id = item?.id || item?.ins_id
            const resource_url = item?.results?.[0] || item?.output || item?.output_with_watermark

            if (!history_id || !resource_url) {
                console.error('copyLink: 缺少必要参数', { history_id, resource_url })
                noticeMessagePopup(
                    t('inspiration.copyFailed'),
                    t('common.tip'),
                    'error',
                    3000
                )
                return
            }

            // 调用 API 获取短链接
            const res = await getCreationShareLinkApi({
                history_id,
                resource_url,
            })

            if (res.status === 200) {
                const path = `${origin}/creation/${res?.data || ''}`

                // 触发分享弹窗
                emitter.emit('open-share-modal', {
                    url: path,
                    type
                })
            } else {
                throw new Error('获取分享链接失败')
            }
        } catch (e) {
            console.error('copyLink failed:', e)
            noticeMessagePopup(
                t('inspiration.copyFailed'),
                t('common.tip'),
                'error',
                3000
            )
        } finally {
            copy_link_loading.value = false
        }
    } else {
        // 直接使用 ins_id（AIMusic 等其他页面）
        const inspirationId = item.ins_id
        const path = `${origin}/creation/${inspirationId}`

        // 触发分享弹窗
        emitter.emit('open-share-modal', {
            url: path,
            type
        })
    }
}

// 文件下载
const downloadMedia = async (url: string, filename: string) => {
    // 显示加载提示 - 给用户即时反馈

    noticeMessagePopup(t('inspiration.preparingDownload'), '', 'success', 3000)

    try {
        // 使用 fetch 获取文件
        const response = await fetch(url, {
            mode: 'cors',
            cache: 'no-cache',
        })

        if (!response.ok) {
            throw new Error(
                `下载失败: ${response.status} ${response.statusText}`
            )
        }

        // 将响应转换为 Blob
        const blob = await response.blob()
        const blobUrl = window.URL.createObjectURL(blob)

        // 创建临时下载链接
        const link = document.createElement('a')
        link.style.display = 'none'
        link.href = blobUrl
        link.download = filename

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        // 释放 Blob URL
        window.URL.revokeObjectURL(blobUrl)
    } catch (error) {
        console.error('下载失败:', error)
        // 关闭加载提示，显示错误消息

        noticeMessagePopup(t('inspiration.downloadFailed'), '', 'error', 3000)
    }
}

// 下载有水印版本
const downloadWithWatermark = () => {
    if (props.showDataItemInfo?.output_with_watermark) {
        const inspirationId = getInspirationId(props.showDataItemInfo)
        const filename = `weryai_inspiration_${inspirationId}_watermark${getInspirationType(props.showDataItemInfo.type)}`
        downloadMedia(props.showDataItemInfo.output_with_watermark, filename)
    }
}

// 下载无水印版本
const downloadWithoutWatermark = () => {
    // 检查用户是否为 VIP 会员
    if (!isVipUser.value) {
        noticeMessagePopup(t('inspiration.vipRequired'), '', 'warning', 3000)
        // 延迟跳转，让用户看到提示
        setTimeout(() => {
            emitOpenGoodsModal()
        }, 1000)
        return
    }

    if (props.showDataItemInfo?.output) {
        const inspirationId = getInspirationId(props.showDataItemInfo)
        const filename = `weryai_inspiration_${inspirationId}_no_watermark${getInspirationType(props.showDataItemInfo.type)}`
        downloadMedia(props.showDataItemInfo.output, filename)
    } else {
        noticeMessagePopup(
            t('inspiration.noWatermarkUrlNotAvailable'),
            '',
            'warning',
            3000
        )
    }
}

// 关闭弹窗/切换数据时，顺带收起菜单，避免状态残留
watch(
    () => props.showDataItemInfo,
    () => {
        isDownloadMenuOpen.value = false
        stopAlmightyReferenceAudio()
    }
)
</script>

<template>
    <!-- 遮罩层 -->
    <div class="outside-mask-container flex items-center justify-center" ref="maskRef" @wheel="preventScroll"
        @click.self="handleCloseMask">
        <div :class="[isMusicType ? 'music-mask' : '']" class="mask" @click="closeDownloadMenu">
            <button class="close-btn" :aria-label="t('common.close')" @click="handleCloseMask">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true">
                    <path
                        d="M0.308838 0.307739C0.719384 -0.102559 1.38467 -0.102635 1.79517 0.307739L6.99829 5.51184L12.2024 0.308716C12.6129 -0.101641 13.2782 -0.101499 13.6887 0.308716C14.0993 0.719337 14.0993 1.3854 13.6887 1.79602L8.4856 6.99915L13.6897 12.2042C14.1002 12.6148 14.1002 13.2799 13.6897 13.6906C13.2791 14.1012 12.6139 14.1012 12.2034 13.6906L6.99829 8.48547L1.79419 13.6915C1.38361 14.1021 0.718438 14.1021 0.307861 13.6915C-0.102587 13.2809 -0.102617 12.6158 0.307861 12.2052L5.51196 6.99915L0.308838 1.79504C-0.101739 1.38442 -0.101739 0.718361 0.308838 0.307739Z"
                        fill="white" />
                </svg>
            </button>
            <!-- 音乐类型：全新布局 -->
            <div v-if="isMusicType" class="music-detail-layout">
                <!-- 左侧播放区域 -->
                <div class="music-left-panel">
                    <!-- 背景层：封面图 + 高斯模糊 -->
                    <div class="music-bg-blur" :style="{ backgroundImage: `url(${musicCoverImage})` }"></div>

                    <!-- 歌词面板：优化 LCP - 使用 v-show 避免重新渲染，添加骨架屏占位符 -->
                    <div v-show="isMusicType" class="lyrics-panel">
                        <!-- 歌曲名称 - 修复标题顺序：从 h3 改为 h2（因为右侧有 h2.song-title-single，这里应该也是 h2） -->
                        <h2 class="lyrics-song-title">
                            <template v-if="musicTitle">{{ musicTitle }}</template>
                            <span v-else class="lyrics-skeleton-title"></span>
                        </h2>
                        <!-- 复制歌词按钮 -->
                        <button v-if="musicLyrics" class="copy-lyrics-btn" @click="copyLyrics">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="2">
                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                            </svg>
                            <span>{{ t('inspiration.copyLyrics') }}</span>
                        </button>
                        <!-- 歌词滚动区域 - LCP 元素：提前渲染占位符，减少 render delay -->
                        <div class="lyrics-display-area">
                            <p v-if="musicLyrics" class="lyrics-display-text">{{ musicLyrics }}</p>
                            <!-- 骨架屏：仅在数据加载中时显示，数据加载完成后（无论有无歌词）都不显示 -->
                            <div v-else-if="!isMusicDataLoaded" class="lyrics-skeleton">
                                <div class="lyrics-skeleton-line" v-for="i in 8" :key="i"></div>
                            </div>
                        </div>
                    </div>

                    <!-- 无歌词时：黑胶唱片容器 -->
                    <div v-if="isMusicType && !musicLyrics" class="vinyl-wrapper">
                        <!-- 黑胶唱片 -->
                        <div class="vinyl-record" :class="{ spinning: isPlaying }">
                            <div class="vinyl-outer"></div>
                            <div class="vinyl-inner">
                                <img v-if="musicCoverImage" :src="musicCoverImage"
                                    :srcset="getMusicCoverSrcset(musicCoverImage)"
                                    sizes="(max-width: 768px) 200px, 400px" alt="Music cover" class="vinyl-cover"
                                    loading="eager" decoding="async" width="400" height="400" />
                                <div v-else class="vinyl-cover-placeholder">
                                    <svg class="w-12 h-12 text-white/30" viewBox="0 0 24 24" fill="currentColor">
                                        <path
                                            d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
                                    </svg>
                                </div>
                            </div>
                        </div>

                        <!-- 唱针 -->
                        <img src="~/assets/images/common/music-open.png" alt="needle" class="music-needle"
                            :class="{ playing: isPlaying }" loading="eager" decoding="async" width="40" height="40" />
                    </div>

                    <!-- 底部控制区域 -->
                    <div class="music-control-panel">
                        <!-- 进度条 -->
                        <div class="progress-row">
                            <span class="time-text">{{
                                formatTime(currentTime)
                                }}</span>
                            <div class="progress-bar" ref="progressBarRef" @click="handleAudioSeek">
                                <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
                                <div class="progress-thumb" :style="{ left: progressPercent + '%' }"></div>
                            </div>
                            <span class="time-text">{{
                                formatTime(duration)
                                }}</span>
                        </div>

                        <!-- 播放按钮组 -->
                        <div class="control-buttons">
                            <!-- 回退5秒 -->
                            <button class="seek-btn" type="button" @click="seekBackward"
                                :aria-label="t('common.backward') || 'Backward 5 seconds'">
                                <img src="~/assets/images/common/seek-backward.png" alt="backward" class="seek-icon"
                                    loading="eager" decoding="async" width="20" height="20" aria-hidden="true" />
                            </button>

                            <!-- 播放/暂停 -->
                            <button class="play-btn-large" type="button" :aria-label="isLoading
                                ? t('common.loading')
                                : isPlaying
                                    ? `${t('common.pause')} ${musicTitle || t('inspiration.untitledMusic')}`
                                    : `${t('common.play')} ${musicTitle || t('inspiration.untitledMusic')}`
                                " @click="toggleMusicPlay">
                                <!-- 加载中 -->
                                <svg v-if="isLoading" class="animate-spin" width="24" height="24" viewBox="0 0 24 24"
                                    fill="none" aria-hidden="true">
                                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"
                                        stroke-opacity="0.3" />
                                    <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" stroke-width="2"
                                        stroke-linecap="round" />
                                </svg>
                                <!-- 播放 -->
                                <svg v-else-if="!isPlaying" width="24" height="24" viewBox="0 0 24 24"
                                    fill="currentColor" aria-hidden="true">
                                    <path d="M8 5v14l11-7z" />
                                </svg>
                                <!-- 暂停 -->
                                <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="currentColor"
                                    aria-hidden="true">
                                    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                                </svg>
                            </button>

                            <!-- 前进5秒 -->
                            <button class="seek-btn" type="button" @click="seekForward"
                                :aria-label="t('common.forward') || 'Forward 5 seconds'">
                                <img src="~/assets/images/common/seek-forward.png" alt="forward" class="seek-icon"
                                    loading="eager" decoding="async" width="20" height="20" aria-hidden="true" />
                            </button>
                        </div>
                    </div>
                </div>

                <!-- 右侧信息区域 -->
                <div class="music-right-panel">
                    <!-- 标题区 -->
                    <div class="music-header">
                        <div class="song-label">SONG TITLE</div>
                        <h2 class="song-title-single">{{ musicTitle }}</h2>
                        <div class="user-info" v-if="showDataItemInfo.user">
                            <img v-if="showDataItemInfo.user.avatar" :src="showDataItemInfo.user.avatar"
                                alt="User avatar" class="user-avatar" />
                            <img v-else
                                src="https://static.weryai.com/growth/image/9ead8a3f9b5dd9d44367d3aba6ac8a93.webp"
                                alt="Default avatar" class="user-avatar" />
                            <span class="user-name">{{
                                showDataItemInfo.user.user_name
                                }}</span>
                        </div>
                    </div>

                    <!-- 内容滚动区域 -->
                    <div class="music-content-scroll">
                        <!-- 提示词区 -->
                        <div class="info-section-fixed">
                            <div class="section-header">
                                <span class="section-label">PROMPT</span>
                                <button class="copy-icon-btn" type="button" :aria-label="t('inspiration.copyPrompt')"
                                    @click="copyPrompt" v-if="showDataItemInfo?.param?.prompt">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                        stroke-width="2" aria-hidden="true">
                                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                                    </svg>
                                </button>
                            </div>
                            <div class="section-content-scroll">
                                {{
                                    showDataItemInfo?.param?.prompt ||
                                    t('inspiration.noPrompt')
                                }}
                            </div>
                        </div>

                        <!-- 参考音频区 -->
                        <div class="info-section-fixed" v-if="referenceAudioUrl">
                            <div class="section-header">
                                <span class="section-label">{{
                                    t('inspiration.referenceAudio')
                                    }}</span>
                            </div>
                            <div class="ref-audio-card">
                                <!-- 播放按钮 -->
                                <button class="ref-audio-play-btn" @click="toggleRefAudioPlay">
                                    <!-- 加载中 -->
                                    <svg v-if="isRefAudioLoading" class="animate-spin" width="16" height="16"
                                        viewBox="0 0 24 24" fill="none">
                                        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"
                                            stroke-opacity="0.3" />
                                        <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" stroke-width="2"
                                            stroke-linecap="round" />
                                    </svg>
                                    <!-- 播放 -->
                                    <svg v-else-if="!isRefAudioPlaying" width="16" height="16" viewBox="0 0 24 24"
                                        fill="currentColor">
                                        <path d="M8 5v14l11-7z" />
                                    </svg>
                                    <!-- 暂停 -->
                                    <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                                    </svg>
                                </button>

                                <!-- 音频名称 -->
                                <span class="ref-audio-name">{{
                                    referenceAudioName
                                    }}</span>

                                <!-- 下载按钮 -->
                                <button class="ref-audio-download-btn" @click="downloadRefAudio">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                        stroke-width="2">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                        <polyline points="7,10 12,15 17,10" />
                                        <line x1="12" y1="15" x2="12" y2="3" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- 底部操作栏 -->
                    <div class="music-footer">
                        <!-- 下载 -->
                        <button class="footer-btn" type="button"
                            :aria-label="`${t('common.download')} ${musicTitle || t('inspiration.untitledMusic')}`"
                            @click="downloadMusic">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="2" aria-hidden="true">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="7,10 12,15 17,10" />
                                <line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                        </button>

                        <!-- 分享链接 -->
                        <button class="footer-btn" type="button" :aria-label="t('inspiration.copyLink')"
                            @click="copyLink">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="2" aria-hidden="true">
                                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                            </svg>
                        </button>

                        <!-- 点赞 -->
                        <button v-if="!hidden_like" class="footer-btn like-btn"
                            :class="{ liked: showDataItemInfo.has_like }" @click="
                                ($event: MouseEvent) =>
                                    emit(
                                        'changeLikeStateForItem',
                                        $event,
                                        showDataItemInfo
                                    )
                            ">
                            <svg v-if="!showDataItemInfo.has_like" width="20" height="20" viewBox="0 0 20 20"
                                fill="none" stroke="currentColor" stroke-width="1.5">
                                <path
                                    d="M10 17.5L8.825 16.45C4.4 12.46 1.5 9.85 1.5 6.5C1.5 3.89 3.39 2 6 2C7.44 2 8.82 2.69 10 3.78C11.18 2.69 12.56 2 14 2C16.61 2 18.5 3.89 18.5 6.5C18.5 9.85 15.6 12.46 11.175 16.45L10 17.5Z" />
                            </svg>
                            <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="#FF5353">
                                <path
                                    d="M10 17.5L8.825 16.45C4.4 12.46 1.5 9.85 1.5 6.5C1.5 3.89 3.39 2 6 2C7.44 2 8.82 2.69 10 3.78C11.18 2.69 12.56 2 14 2C16.61 2 18.5 3.89 18.5 6.5C18.5 9.85 15.6 12.46 11.175 16.45L10 17.5Z" />
                            </svg>
                            <span>{{ showDataItemInfo.likes_num || 0 }}</span>
                        </button>

                        <!-- 做同款 -->
                        <button class="make-similar-btn" @click="handleMusicMakeSimilar">
                            <img src="~/assets/images/common/make-similar-icon.png" alt="make similar"
                                class="make-similar-icon" />
                            <span>{{
                                t('menus.aiMusicPage.makeSimilar')
                                }}</span>
                        </button>
                    </div>
                </div>
            </div>

            <!-- 非音乐类型：原有布局 -->
            <div v-else class="detail-info-model">
                <!-- 详细内容区域 -->
                <div class="show-plate" :style="{ aspectRatio: mediaRenderMeta.aspectRatio }">
                    <!-- 图片/视频类型：展示 -->
                    <div class="media-loading" v-if="mediaLoading">
                        <l-dot-spinner size="50" speed="0.9" color="var(--el-menu-active-color)"></l-dot-spinner>
                    </div>
                    <img v-if="!isMainMediaVideo" :class="{ 'is-loading': mediaLoading }" class="object-contain"
                        :src="showDataItemInfo.output"
                        :srcset="getImageSrcset(showDataItemInfo.output)"
                        sizes="(max-width: 768px) 100vw, (max-width: 1024px) 90vw, 80vw" :alt="generateAltText"
                        :width="mediaRenderMeta.width" :height="mediaRenderMeta.height"
                        ref="mainMediaRef" loading="eager" decoding="async" @load="handleMediaLoaded"
                        @error="handleMediaError" />
                    <video v-else :src="showDataItemInfo.output" class="object-contain" :poster="videoPosterUrl"
                        muted autoplay playsinline preload="metadata" :controls="!mediaLoading"
                        :class="{ 'is-loading': mediaLoading }" ref="mainMediaRef" @loadeddata="handleMediaLoaded"
                        @error="handleMediaError"></video>
                </div>
                <div class="detail-info">
                    <header>
                        <div class="create-type">
                            <img v-if="showDataItemInfo.user?.avatar" :src="showDataItemInfo.user?.avatar"
                                :alt="userAvatarAlt" class="avatar" loading="lazy" decoding="async" width="40"
                                height="40" />
                            <img v-else
                                src="https://static.weryai.com/growth/image/9ead8a3f9b5dd9d44367d3aba6ac8a93.webp"
                                alt="Default user avatar" class="avatar" loading="lazy" decoding="async" width="40"
                                height="40" />

                            <div class="create">
                                <div class="username max-w-[200px]">
                                    {{ showDataItemInfo.user?.user_name || '' }}
                                </div>
                                <div class="create-time">
                                    {{
                                        formatDate(showDataItemInfo.create_time)
                                    }}
                                </div>
                            </div>
                        </div>
                    </header>
                    <main>
                        <div class="type-label" v-if="showDataItemInfo.type">
                            {{
                                is_characters_type
                                    ? t('characterToImg.characterToImage')
                                    : showDataItemInfo.type_name
                            }}
                        </div>

                        <!-- 图片/视频类型：信息展示 -->
                        <div v-if="isAlmightyReference && almightyReferenceItems.length" class="almighty-reference">
                            <div class="title">
                                {{ t('workbench.seedanceVideoModes.allRound') }}
                            </div>
                            <div class="almighty-reference-list">
                                <template v-for="previewItem in almightyReferenceItems" :key="previewItem.id">
                                    <div
                                        v-if="previewItem.kind === 'image'"
                                        class="almighty-reference-card almighty-reference-card--visual"
                                        @click="openAlmightyReferencePreview(previewItem)"
                                    >
                                        <img :src="previewItem.url" :alt="previewItem.label"
                                            class="almighty-reference-card__cover" />
                                        <div class="almighty-reference-card__overlay"></div>
                                        <div class="almighty-reference-card__label">{{ previewItem.label }}</div>
                                    </div>

                                    <div
                                        v-else-if="previewItem.kind === 'video'"
                                        class="almighty-reference-card almighty-reference-card--visual"
                                        @click="openAlmightyReferencePreview(previewItem)"
                                    >
                                        <img :src="previewItem.posterUrl || previewItem.url" :alt="previewItem.label"
                                            class="almighty-reference-card__cover" />
                                        <div class="almighty-reference-card__overlay"></div>
                                        <div class="almighty-reference-card__play">
                                            <svg width="22" height="22" viewBox="0 0 22 22" fill="none"
                                                xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                                <circle cx="11" cy="11" r="11" fill="rgba(0, 0, 0, 0.3)" />
                                                <path d="M8.75 6.875L15.125 11L8.75 15.125V6.875Z" fill="white" />
                                            </svg>
                                        </div>
                                        <div class="almighty-reference-card__label">{{ previewItem.label }}</div>
                                        <div
                                            v-if="getAlmightyDurationText(getResolvedAlmightyDuration(previewItem))"
                                            class="almighty-reference-card__duration"
                                        >
                                            {{ getAlmightyDurationText(getResolvedAlmightyDuration(previewItem)) }}
                                        </div>
                                    </div>

                                    <button v-else class="almighty-reference-card almighty-reference-card--audio"
                                        type="button" @click.stop="toggleAlmightyAudioPreview(previewItem)">
                                        <div class="almighty-reference-card__label">{{ previewItem.label }}</div>
                                        <div class="almighty-reference-card__audio-icon">
                                            <img :src="ALMIGHTY_AUDIO_ICON_URL" :alt="previewItem.label" />
                                        </div>
                                        <div class="almighty-reference-card__audio-progress">
                                            <span class="almighty-reference-card__audio-play">
                                                <svg v-if="!isAlmightyAudioPlaying(previewItem)" width="8" height="8"
                                                    viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg"
                                                    aria-hidden="true">
                                                    <path d="M2 1.5L6 4L2 6.5V1.5Z" fill="white" />
                                                </svg>
                                                <svg v-else width="8" height="8" viewBox="0 0 8 8" fill="none"
                                                    xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                                    <rect x="1.5" y="1.5" width="1.5" height="5" fill="white" />
                                                    <rect x="5" y="1.5" width="1.5" height="5" fill="white" />
                                                </svg>
                                            </span>
                                            <span class="almighty-reference-card__audio-time">
                                                {{ getAlmightyAudioCurrentTimeText(previewItem) }}
                                            </span>
                                            <span class="almighty-reference-card__audio-separator">/</span>
                                            <span class="almighty-reference-card__audio-total">
                                                {{
                                                    getAlmightyDurationText(
                                                        getResolvedAlmightyDuration(previewItem)
                                                    ) || '--:--'
                                                }}
                                            </span>
                                        </div>
                                    </button>
                                </template>
                            </div>
                        </div>
                        <div v-else class="origin-imgs">
                            <div class="title" v-if="showDataItemInfo.param?.pic_urls?.length">
                                {{ t('inspiration.originalImage') }}
                            </div>
                            <div class="imgs-container">
                                <img :src="item" :alt="generateRefImageAlt(index)" class="object-contain" v-for="(item, index) in showDataItemInfo
                                    .param?.pic_urls" :key="item" />
                            </div>
                        </div>
                        <div class="prompt" v-show="showDataItemInfo?.param?.prompt">
                            <div class="title-info">
                                <div class="title">
                                    {{ t('inspiration.prompt') }}
                                </div>
                                <button class="copy-btn" :aria-label="t('inspiration.copyPrompt') ||
                                    t('common.copy')
                                    " @click.stop="copyPrompt">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                        stroke-linejoin="round" aria-hidden="true">
                                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                    </svg>
                                </button>
                            </div>
                            <div class="info" :class="{ 'info--almighty-rich': hasRichAlmightyPromptSegments }">
                                <template v-if="hasRichAlmightyPromptSegments">
                                    <template v-for="segment in almightyPromptSegments" :key="segment.key">
                                        <span v-if="segment.type === 'text'" class="prompt-rich-text">
                                            {{ segment.text }}
                                        </span>
                                        <button v-else type="button" class="prompt-mention-chip"
                                            :class="`prompt-mention-chip--${segment.item.kind}`"
                                            :aria-label="segment.label"
                                            @click.stop="handlePromptMentionClick(segment.item)">
                                            <span class="prompt-mention-chip__media" aria-hidden="true">
                                                <img v-if="segment.item.kind === 'image'" :src="segment.item.url"
                                                    :alt="segment.label" class="prompt-mention-chip__thumb" />
                                                <span v-else-if="segment.item.kind === 'video'"
                                                    class="prompt-mention-chip__video-thumb">
                                                    <img :src="segment.item.posterUrl || segment.item.url"
                                                        :alt="segment.label" class="prompt-mention-chip__thumb" />
                                                    <span class="prompt-mention-chip__video-play">
                                                        <svg width="8" height="8" viewBox="0 0 8 8" fill="none"
                                                            xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M2 1.5L6 4L2 6.5V1.5Z" fill="white" />
                                                        </svg>
                                                    </span>
                                                </span>
                                                <img v-else :src="ALMIGHTY_AUDIO_ICON_URL" :alt="segment.label"
                                                    class="prompt-mention-chip__audio-icon" />
                                            </span>
                                            <span class="prompt-mention-chip__label">
                                                {{ segment.label }}
                                            </span>
                                        </button>
                                    </template>
                                </template>
                                <template v-else>
                                    {{ showDataItemInfo?.param?.prompt || '' }}
                                </template>
                            </div>
                        </div>

                        <div class="image-info">
                            <div class="info-row" v-show="showDataItemInfo?.param?.model">
                                <span class="label">{{ t('inspiration.model') }}:</span>
                                <span class="value">{{
                                    showDataItemInfo?.param?.model || ''
                                    }}</span>
                            </div>
                            <div class="info-row" v-show="false">
                                <span class="label">{{ t('inspiration.style') }}:</span>
                                <span class="value">{{
                                    t('inspiration.auto')
                                    }}</span>
                            </div>
                            <div class="info-row" v-if="showDataItemInfo?.param?.aspect_ratio">
                                <span class="label">{{ t('inspiration.aspectRatio') }}:</span>
                                <span class="value">{{
                                    showDataItemInfo?.param?.aspect_ratio
                                    }}</span>
                            </div>
                            <div class="info-row" v-if="outputSize">
                                <span class="label">{{ t('inspiration.outputSize') }}:</span>
                                <span class="value">{{ outputSize }}</span>
                            </div>
                        </div>

                        <div class="tags-section" v-show="false">
                            <div class="tags-title">
                                {{ t('inspiration.tags') }}
                            </div>
                            <div class="tags-list">
                                <span class="tag-item" v-for="(
tag, index
                                    ) in showDataItemInfo.tags" :key="index">
                                    {{ tag }}
                                </span>
                            </div>
                        </div>
                        <div class="tags-section" v-if="
                            getInspirationType(showDataItemInfo.type) ===
                            'IMAGE' ||
                            getInspirationOutputType(
                                showDataItemInfo.output
                            ) === 'IMAGE'
                        ">
                            <div class="tags-title">
                                {{ t('inspiration.generate') }}
                            </div>
                            <div class="tags-list">
                                <span class="tag-item" v-for="item in generate_type_list" :key="item.value" @click="
                                    imageToGenerate(
                                        getInspirationId(showDataItemInfo),
                                        item.value
                                    )
                                    ">
                                    {{ item.label }}
                                </span>
                            </div>
                        </div>
                    </main>

                    <footer>
                        <div class="bottom-operation">
                            <div class="left-operate">
                                <button class="like" :aria-label="showDataItemInfo.has_like
                                    ? t('common.unlike')
                                    : t('common.like')
                                    " @click="
                                        ($event: MouseEvent) =>
                                            emit(
                                                'changeLikeStateForItem',
                                                $event,
                                                showDataItemInfo
                                            )
                                    ">
                                    {{ showDataItemInfo.likes_num }}
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20"
                                        fill="none" v-show="!showDataItemInfo.has_like" aria-hidden="true">
                                        <path
                                            d="M10 17.5L8.825 16.45C4.4 12.46 1.5 9.85 1.5 6.5C1.5 3.89 3.39 2 6 2C7.44 2 8.82 2.69 10 3.78C11.18 2.69 12.56 2 14 2C16.61 2 18.5 3.89 18.5 6.5C18.5 9.85 15.6 12.46 11.175 16.45L10 17.5Z"
                                            stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
                                            stroke-linejoin="round" fill="none" />
                                    </svg>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20"
                                        fill="none" v-show="showDataItemInfo.has_like" aria-hidden="true">
                                        <path
                                            d="M10 17.5L8.825 16.45C4.4 12.46 1.5 9.85 1.5 6.5C1.5 3.89 3.39 2 6 2C7.44 2 8.82 2.69 10 3.78C11.18 2.69 12.56 2 14 2C16.61 2 18.5 3.89 18.5 6.5C18.5 9.85 15.6 12.46 11.175 16.45L10 17.5Z"
                                            fill="#FF5353" />
                                    </svg>
                                </button>

                                <button class="copyLink" :aria-label="t('inspiration.copyLink')" @click="copyLink">
                                    <svg width="20" height="20" viewBox="0 0 14 14" fill="none"
                                        xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                        <path
                                            d="M3.28718 7.00001C2.79481 6.50765 2.51821 5.83986 2.51821 5.14355C2.51821 4.44725 2.79481 3.77946 3.28718 3.2871C3.77954 2.79473 4.44733 2.51813 5.14363 2.51813C5.83994 2.51813 6.50773 2.79473 7.00009 3.2871L7.92759 4.2146C8.03919 4.31016 8.18273 4.3601 8.32954 4.35443C8.47635 4.34876 8.61562 4.2879 8.7195 4.18401C8.82339 4.08012 8.88425 3.94085 8.88992 3.79404C8.8956 3.64723 8.84566 3.50369 8.75009 3.3921L7.82551 2.46168C7.46784 2.10279 7.04122 1.82003 6.57134 1.63043C5.65501 1.26292 4.63226 1.26292 3.71593 1.63043C3.24665 1.82111 2.82033 2.10389 2.46215 2.46207C2.10397 2.82025 1.82119 3.24657 1.63051 3.71585C1.26118 4.63182 1.26118 5.65528 1.63051 6.57126C1.82011 7.04114 2.10287 7.46776 2.46176 7.82543L3.39218 8.75001C3.50332 8.85037 3.64879 8.90413 3.79848 8.90019C3.94818 8.89624 4.09062 8.83487 4.19631 8.7288C4.30201 8.62273 4.36287 8.48007 4.36629 8.33037C4.3697 8.18067 4.31542 8.03538 4.21468 7.9246L3.28718 7.00001ZM12.3697 7.42876C12.1801 6.95889 11.8973 6.53227 11.5384 6.1746L10.608 5.25001C10.5548 5.19155 10.4904 5.1445 10.4185 5.11171C10.3466 5.07893 10.2688 5.06109 10.1898 5.05929C10.1108 5.05749 10.0322 5.07175 9.95889 5.10122C9.88557 5.13069 9.81901 5.17475 9.76323 5.23072C9.70745 5.2867 9.66363 5.35342 9.63442 5.42684C9.60521 5.50027 9.59122 5.57886 9.59331 5.65785C9.59539 5.73684 9.6135 5.81459 9.64654 5.88637C9.67958 5.95815 9.72686 6.02247 9.78551 6.07543L10.713 7.00001C10.9568 7.24381 11.1502 7.53323 11.2821 7.85176C11.4141 8.17029 11.482 8.5117 11.482 8.85647C11.482 9.20125 11.4141 9.54265 11.2821 9.86118C11.1502 10.1797 10.9568 10.4691 10.713 10.7129C10.4692 10.9567 10.1798 11.1501 9.86126 11.2821C9.54273 11.414 9.20133 11.4819 8.85655 11.4819C8.51178 11.4819 8.17037 11.414 7.85184 11.2821C7.53331 11.1501 7.24389 10.9567 7.00009 10.7129L6.07259 9.78543C6.0203 9.72437 5.95595 9.67477 5.88358 9.63976C5.8112 9.60474 5.73238 9.58507 5.65204 9.58196C5.5717 9.57886 5.49159 9.5924 5.41674 9.62172C5.34188 9.65105 5.2739 9.69553 5.21705 9.75238C5.1602 9.80923 5.11571 9.87722 5.08639 9.95208C5.05706 10.0269 5.04352 10.107 5.04663 10.1874C5.04973 10.2677 5.06941 10.3465 5.10442 10.4189C5.13943 10.4913 5.18903 10.5556 5.25009 10.6079L6.17759 11.5383C6.53526 11.8972 6.96189 12.18 7.43176 12.3696C8.34774 12.7389 9.3712 12.7389 10.2872 12.3696C10.7565 12.1789 11.1828 11.8961 11.541 11.538C11.8991 11.1798 12.1819 10.7535 12.3726 10.2842C12.7419 9.3682 12.7419 8.34474 12.3726 7.42876H12.3697Z"
                                            fill="currentColor" />
                                        <path
                                            d="M8.85566 9.43832C8.96801 9.43574 9.07723 9.40076 9.17017 9.33759C9.26312 9.27442 9.33584 9.18574 9.37958 9.08223C9.42333 8.97871 9.43625 8.86476 9.41678 8.75408C9.39731 8.6434 9.34628 8.5407 9.26983 8.45832L5.55691 4.73082C5.50272 4.67663 5.43837 4.63364 5.36756 4.6043C5.29675 4.57497 5.22085 4.55988 5.1442 4.55988C5.06756 4.55988 4.99166 4.57497 4.92085 4.6043C4.85004 4.63364 4.78569 4.67663 4.7315 4.73082C4.6773 4.78502 4.63431 4.84936 4.60498 4.92018C4.57564 4.99099 4.56055 5.06689 4.56055 5.14353C4.56055 5.22018 4.57564 5.29608 4.60498 5.36689C4.63431 5.4377 4.6773 5.50204 4.7315 5.55624L8.459 9.26916C8.56476 9.37414 8.70669 9.43467 8.85566 9.43832Z"
                                            fill="currentColor" />
                                    </svg>
                                </button>

                                <!-- 音乐类型：直接下载按钮 -->
                                <button v-if="isMusicType" class="download" :aria-label="t('inspiration.download')"
                                    @click="downloadMusic">
                                    <svg width="20" height="20" viewBox="0 0 16 16" fill="none"
                                        xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                        <path
                                            d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10"
                                            stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
                                            stroke-linejoin="round" />
                                        <path d="M4.66699 6.66675L8.00033 10.0001L11.3337 6.66675" stroke="currentColor"
                                            stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                        <path d="M8 2V10" stroke="currentColor" stroke-width="1.5"
                                            stroke-linecap="round" stroke-linejoin="round" />
                                    </svg>
                                </button>

                                <!-- 图片/视频类型：下载菜单 -->
                                <div v-else class="download-wrapper">
                                    <button class="download" :aria-label="t('inspiration.download')"
                                        @click.stop="toggleDownloadMenu">
                                        <svg width="20" height="20" viewBox="0 0 16 16" fill="none"
                                            xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                            <path
                                                d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10"
                                                stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
                                                stroke-linejoin="round" />
                                            <path d="M4.66699 6.66675L8.00033 10.0001L11.3337 6.66675"
                                                stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
                                                stroke-linejoin="round" />
                                            <path d="M8 2V10" stroke="currentColor" stroke-width="1.5"
                                                stroke-linecap="round" stroke-linejoin="round" />
                                        </svg>
                                    </button>

                                    <div class="download-menu-native" role="menu" :class="{ open: isDownloadMenuOpen }"
                                        @click.stop>
                                        <button v-if="
                                            showDataItemInfo?.output_with_watermark
                                        " class="download-item-native" @click="downloadWithWatermark">
                                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none"
                                                xmlns="http://www.w3.org/2000/svg">
                                                <path
                                                    d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10"
                                                    stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
                                                    stroke-linejoin="round" />
                                                <path d="M4.66699 6.66675L8.00033 10.0001L11.3337 6.66675"
                                                    stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
                                                    stroke-linejoin="round" />
                                                <path d="M8 2V10" stroke="currentColor" stroke-width="1.5"
                                                    stroke-linecap="round" stroke-linejoin="round" />
                                            </svg>
                                            <span>{{
                                                t(
                                                    'inspiration.downloadWithWatermark'
                                                )
                                            }}</span>
                                        </button>
                                        <button class="download-item-native" @click="downloadWithoutWatermark">
                                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none"
                                                xmlns="http://www.w3.org/2000/svg">
                                                <path
                                                    d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10"
                                                    stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
                                                    stroke-linejoin="round" />
                                                <path d="M4.66699 6.66675L8.00033 10.0001L11.3337 6.66675"
                                                    stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
                                                    stroke-linejoin="round" />
                                                <path d="M8 2V10" stroke="currentColor" stroke-width="1.5"
                                                    stroke-linecap="round" stroke-linejoin="round" />
                                            </svg>
                                            <span>{{
                                                t(
                                                    'inspiration.downloadWithoutWatermark'
                                                )
                                            }}</span>
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"
                                                xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                                <path
                                                    d="M1.87051 3.46688L4.67003 5.33323L7.46087 1.42605C7.67487 1.12644 8.09127 1.05705 8.39087 1.27105C8.45073 1.31381 8.50307 1.36618 8.54587 1.42605L11.3367 5.33323L14.1362 3.46688C14.4426 3.26265 14.8565 3.34543 15.0607 3.65178C15.1485 3.78353 15.1866 3.94223 15.1681 4.09947L14.0727 13.4111C14.0331 13.7468 13.7486 13.9999 13.4105 13.9999H2.5962C2.25815 13.9999 1.9736 13.7468 1.9341 13.4111L0.838615 4.09947C0.795595 3.73381 1.05715 3.4025 1.42282 3.35948C1.58007 3.34098 1.73877 3.37905 1.87051 3.46688ZM8.0034 9.99991C8.73973 9.99991 9.33673 9.40297 9.33673 8.66657C9.33673 7.93017 8.73973 7.33324 8.0034 7.33324C7.267 7.33324 6.67007 7.93017 6.67007 8.66657C6.67007 9.40297 7.267 9.99991 8.0034 9.99991Z"
                                                    fill="white" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <!-- 图片/视频类型：生成相似按钮 -->
                            <button v-if="!isMusicType && canGenerateSimilar" class="right-create"
                                @click="navigationChange(showDataItemInfo)">
                                {{ t('inspiration.generateSimilar') }}
                            </button>
                        </div>
                    </footer>
                </div>
            </div>
        </div>
    </div>
</template>
<style scoped lang="less">
.outside-mask-container {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.7);
    z-index: 101;
    overflow: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
}

.mask {
    position: absolute;
    top: 15%;
    bottom: 15%;
    left: 15%;
    right: 15%;
    width: auto;
    height: auto;
    padding: 20px;
    overflow: auto;
    border-radius: 24px;
    background-color: #22242d;
    // 确保默认文本颜色有足够对比度
    color: #ffffff; // 白色文本在深色背景上对比度足够

    // 音乐类型：居中显示，固定最大宽高
    &.music-mask {
        position: relative;
        top: auto;
        bottom: auto;
        left: auto;
        right: auto;
        width: 100%;
        max-width: 988px;
        height: 100%;
        max-height: 774px;
        overflow: hidden;
    }

    .close-btn {
        position: absolute;
        right: 20px;
        top: 20px;
        z-index: 10;
        border-radius: 5px;
        border: none;
        background: transparent;
        cursor: pointer;
        color: #cfd3dc;

        &:hover {
            background: #22242d;
            color: #fff;
        }
    }

    .detail-info-model {
        display: flex;
        column-gap: 20px;
        position: relative;
        width: 100%;
        height: 100%;
        z-index: 1;

        .show-plate {
            flex: 1;
            background-color: #0d0e12;
            border-radius: 10px;
            overflow: hidden;
            position: relative;

            img,
            video {
                max-height: 100%;
                max-width: 100%;
                margin: 0 auto;
                display: block;
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
            }

            .media-loading {
                position: absolute;
                inset: 0;
                display: flex;
                align-items: center;
                justify-content: center;
                background: #0d0e12;
                z-index: 2;
            }

            .is-loading {
                opacity: 0;
            }

            video.is-loading::-webkit-media-controls-enclosure,
            video.is-loading::-webkit-media-controls-panel,
            video.is-loading::-webkit-media-controls {
                display: none !important;
            }
        }
    }
}

.detail-info {
    position: relative;
    width: 384px;
    display: flex;
    flex-direction: column;
    color: #cfd3dc;
    justify-content: space-between;

    header {
        /* border-bottom: 1px solid #3a3d4a; */
        margin-bottom: 10px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-shrink: 0;
        height: 32px;

        .create-type {
            display: flex;
            align-items: center;
            gap: 8px;

            img {
                width: 32px;
                height: 32px;
                border-radius: 50%;
                object-fit: cover;
                flex-shrink: 0;
            }

            .create {
                flex: 1;
                min-width: 0;
                overflow: hidden;
            }

            .username {
                font-size: 0.8125rem;
                font-weight: 400;
                color: #fff;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            .create-time {
                font-size: 0.6875rem;
                color: rgba(255, 255, 255, 0.6);
                font-weight: 400;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }
        }
    }

    main {
        flex: 1;
        overflow-y: auto;
        padding: 12px;
        background-color: #22242d;
        min-height: 0;
        /* max-height: 60%; */
        padding-left: 0;
        border-top: 1px solid #383a42;
        margin-top: 15px;
        font-weight: 500;
        overscroll-behavior: contain;
        -webkit-overflow-scrolling: touch;

        .type-label {
            font-size: 0.8125rem;
            padding: 0px 5px;
            width: fit-content;
            border-radius: 6px;
            margin-bottom: 10px;
            background: #333643;
            padding: 5px 8px;
            color: #fff;
        }

        /* 原图 */
        .origin-imgs,
        .almighty-reference {
            .title {
                font-size: 13px;
                font-weight: 600;
                line-height: normal;
                color: #fff;
                margin-bottom: 12px;
            }
        }

        .almighty-reference {
            margin-bottom: 8px;

            .almighty-reference-list {
                display: flex;
                flex-wrap: wrap;
                gap: 9.231px;
                width: 100%;
            }

            .almighty-reference-card {
                position: relative;
                width: 83.077px;
                height: 83.077px;
                border-radius: 9.231px;
                overflow: hidden;
                border: 0.923px solid rgba(255, 255, 255, 0.1);
                background: #1c1c20;
                flex-shrink: 0;
            }

            .almighty-reference-card--visual {
                cursor: pointer;
                transition: border-color 0.2s ease;

                &:hover {
                    border-color: rgba(255, 255, 255, 0.18);
                }
            }

            .almighty-reference-card__cover {
                width: 100%;
                height: 100%;
                object-fit: cover;
                display: block;
            }

            .almighty-reference-card__overlay {
                position: absolute;
                inset: 0;
                background: linear-gradient(
                    180deg,
                    rgba(0, 0, 0, 0.4) 0%,
                    rgba(0, 0, 0, 0) 29.81%,
                    rgba(0, 0, 0, 0) 74.04%,
                    rgba(0, 0, 0, 0.4) 100%
                );
            }

            .almighty-reference-card__label {
                position: absolute;
                top: 6px;
                left: 5.54px;
                right: 5.54px;
                font-size: 10px;
                line-height: 1;
                color: #fff;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                z-index: 1;
            }

            .almighty-reference-card__play {
                position: absolute;
                left: 50%;
                top: 50%;
                transform: translate(-50%, -50%);
                display: flex;
                align-items: center;
                justify-content: center;
                z-index: 1;
                pointer-events: none;
            }

            .almighty-reference-card__duration {
                position: absolute;
                left: 5.54px;
                bottom: 6px;
                font-size: 10px;
                line-height: 1;
                color: #fff;
                z-index: 1;
            }

            .almighty-reference-card--audio {
                padding: 0;
                cursor: pointer;
                appearance: none;
                outline: none;

                .almighty-reference-card__label {
                    left: 7.38px;
                    right: 7.38px;
                }

                &:hover {
                    border-color: rgba(255, 255, 255, 0.18);
                }
            }

            .almighty-reference-card__audio-icon {
                position: absolute;
                left: 50%;
                top: 50%;
                transform: translate(-50%, -58%);
                width: 29.777px;
                height: 29.777px;
                display: flex;
                align-items: center;
                justify-content: center;

                img {
                    width: 100%;
                    height: 100%;
                    object-fit: contain;
                }
            }

            .almighty-reference-card__audio-progress {
                position: absolute;
                left: 50%;
                top: 60px;
                transform: translateX(-50%);
                width: 77.538px;
                min-height: 16px;
                padding: 3.692px 10px;
                border-radius: 5.538px;
                background: rgba(255, 255, 255, 0.05);
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 3.692px;
                z-index: 1;
                box-sizing: border-box;
            }

            .almighty-reference-card__audio-play {
                width: 10px;
                height: 10px;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                color: #fff;
                flex-shrink: 0;
            }

            .almighty-reference-card__audio-time,
            .almighty-reference-card__audio-separator,
            .almighty-reference-card__audio-total {
                font-size: 8px;
                line-height: 1;
                white-space: nowrap;
            }

            .almighty-reference-card__audio-time {
                color: #fff;
            }

            .almighty-reference-card__audio-separator,
            .almighty-reference-card__audio-total {
                color: rgba(255, 255, 255, 0.3);
            }
        }

        .origin-imgs {
            .imgs-container {
                display: flex;
                grid-template-columns: repeat(3, 1fr);
                gap: 5px;
                flex-wrap: wrap;
            }

            img {
                max-height: 82px;
                width: auto;
                border-radius: 8px;
                /* margin-right: 5px; */
            }
        }

        .prompt {
            /* margin-bottom: 24px; */
            margin: 5px 0;

            .title-info {
                display: flex;
                justify-content: space-between;
                align-items: center;

                .title {
                    font-size: 1rem;
                    font-weight: 500;
                    color: #fff;
                }

                .copy-btn {
                    background: transparent;
                    border: none;
                    color: #8b8d98;
                    cursor: pointer;
                    padding: 4px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 4px;
                    transition: all 0.2s;

                    &:hover {
                        background: #3a3d4a;
                        color: #fff;
                    }

                    svg {
                        width: 16px;
                        height: 16px;
                    }
                }
            }

            .info {
                font-size: 0.8125rem;
                line-height: 1.5;
                color: rgba(255, 255, 255, 0.6);
                max-height: 150px;
                overflow-y: auto;
                font-weight: 400;
                overscroll-behavior: contain;
                -webkit-overflow-scrolling: touch;

                &.info--almighty-rich {
                    display: flex;
                    flex-wrap: wrap;
                    align-items: center;
                    gap: 8px 6px;
                }
            }

            .prompt-rich-text {
                white-space: break-spaces;
                word-break: break-word;
                color: #fff;
            }

            .prompt-mention-chip {
                display: inline-flex;
                align-items: center;
                gap: 4px;
                min-height: 20px;
                padding: 0;
                border: none;
                border-radius: 0;
                background: transparent;
                color: rgba(255, 255, 255, 0.6);
                cursor: pointer;
                transition: all 0.2s ease;
                vertical-align: middle;

                &:hover {
                    color: rgba(255, 255, 255, 0.75);
                }
            }

            .prompt-mention-chip__media {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                width: 20px;
                height: 20px;
                flex-shrink: 0;
                border-radius: 4px;
                overflow: hidden;
                background: rgba(255, 255, 255, 0.08);
            }

            .prompt-mention-chip__thumb,
            .prompt-mention-chip__audio-icon {
                width: 100%;
                height: 100%;
                object-fit: cover;
                display: block;
            }

            .prompt-mention-chip__video-thumb {
                position: relative;
                width: 100%;
                height: 100%;
                display: inline-flex;
            }

            .prompt-mention-chip__video-play {
                position: absolute;
                inset: 0;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                background: rgba(0, 0, 0, 0.24);
            }

            .prompt-mention-chip__label {
                font-size: 12px;
                line-height: 16px;
                font-weight: 500;
                white-space: nowrap;
                color: inherit;
            }
        }

        .image-info {
            margin-bottom: 24px;

            .info-row {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 2px 0;

                &:last-child {
                    border-bottom: none;
                }

                .label {
                    font-size: 1rem;
                    color: #fff;
                    font-weight: 500;
                }

                .value {
                    font-size: 0.8125rem;
                    /* 提升可读性：在 #22242d 背景上确保对比度达到 WCAG AA */
                    color: rgba(255, 255, 255, 0.6);
                    font-weight: 400;
                }
            }
        }
    }

    footer {
        align-items: center;
        min-height: 30px;
        flex-shrink: 0;
        margin-top: 10px;
        user-select: none;

        .generator {
            .title {
                font-size: 1rem;
            }
        }

        .bottom-operation {
            display: flex;
            /* width: 100%; */
            justify-content: space-between;
            margin-top: 10px;
        }

        .left-operate {
            display: flex;
            align-items: center;
            /* gap: 15px; */
        }

        .like {
            cursor: pointer;
            display: flex;
            font-size: 1rem;
            width: fit-content;
            column-gap: 5px;
            align-items: center;
            padding: 0 8px;
            border-radius: 6px;
            height: 26px;
            color: #adadad;

            &:hover {
                background-color: #2d2e3a;
                color: #fff;
            }
        }

        .copyLink {
            cursor: pointer;
            display: flex;
            font-size: 0.875rem;
            width: fit-content;
            column-gap: 5px;
            align-items: center;
            padding: 0 8px;
            border-radius: 6px;
            height: 26px;
            color: #adadad;
            transition: all 0.2s;

            &:hover {
                background-color: #2d2e3a;
                color: #fff;
            }

            svg {
                flex-shrink: 0;

                path {
                    fill: currentColor;
                    transition: fill 0.2s;
                }
            }
        }

        .download-wrapper {
            position: relative;

            &:hover {
                .download-menu-native {
                    opacity: 1;
                    visibility: visible;
                    pointer-events: auto;
                }
            }
        }

        .download {
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 4px 8px;
            border-radius: 6px;
            transition: all 0.2s;
            color: #adadad;
            height: 26px;

            svg {
                display: block;
            }
        }

        .right-create {
            font-size: 0.875rem;
            padding: 8px 18px;
            background: #99f768;
            color: #0d0e12;
            font-weight: 400;
            border-radius: 10px;
            cursor: pointer;
        }
    }
}

.download-menu-native {
    position: absolute;
    bottom: calc(100% + 12px);
    left: 50%;
    transform: translateX(-50%) translateY(4px);
    background: #2a2c36;
    border: 1px solid #3a3d4a;
    border-radius: 8px;
    min-width: 120px;
    opacity: 0;
    visibility: hidden;
    transition: all 0.2s ease;
    z-index: 1000;
    pointer-events: none;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    padding: 6px;

    /* 小三角箭头 */
    &::after {
        content: '';
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        border: 7px solid transparent;
        border-top-color: #3a3d4a;
        margin-top: -1px;
    }

    &::before {
        content: '';
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        border: 6px solid transparent;
        border-top-color: #2a2c36;
        z-index: 1;
    }

    &.open {
        opacity: 1;
        visibility: visible;
        pointer-events: auto;
        transform: translateX(-50%) translateY(0);
    }
}

.download-item-native {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 3px 5px;
    cursor: pointer;
    border-radius: 6px;
    transition: all 0.2s;
    color: #cfd3dc;
    font-size: 0.875rem;
    user-select: none;

    &:hover {
        background: #3a3d4a;
        color: #fff;
    }

    &:not(:last-child) {
        margin-bottom: 4px;
    }

    svg {
        flex-shrink: 0;
    }

    span {
        white-space: nowrap;
    }
}

.menu-item-content {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;

    svg {
        flex-shrink: 0;
    }

    span {
        flex: 1;
        white-space: nowrap;
    }
}

.download-menu-wrapper {
    position: relative;

    &:hover {
        .submenu-native {
            opacity: 1;
            visibility: visible;
            pointer-events: auto;
        }
    }
}

.submenu-native {
    position: absolute;
    left: calc(100% + 8px);
    top: -6px;
    background: #2a2c36;
    border: 1px solid #3a3d4a;
    border-radius: 8px;
    padding: 6px;
    min-width: 150px;
    opacity: 0;
    visibility: hidden;
    transition: all 0.2s ease;
    pointer-events: none;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
    z-index: 10000;
}

.submenu-item-native {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    cursor: pointer;
    border-radius: 6px;
    transition: all 0.2s;
    color: #cfd3dc;
    font-size: 0.8125rem;
    user-select: none;

    &:hover {
        background: #3a3d4a;
        color: #99f768;
    }

    &:not(:last-child) {
        margin-bottom: 2px;
    }

    svg {
        flex-shrink: 0;
    }

    span {
        white-space: nowrap;
    }
}

:deep(.el-dropdown-menu) {
    background: #2a2c36 !important;
    border: 1px solid #3a3d4a !important;
    padding: 6px !important;

    .el-dropdown-menu__item {
        color: #cfd3dc !important;
        font-size: 0.8125rem !important;
        padding: 10px 12px !important;
        border-radius: 6px !important;

        &:hover {
            background: #3a3d4a !important;
            color: #99f768 !important;
        }

        &:not(:last-child) {
            margin-bottom: 2px;
        }
    }
}

.tags-section {
    .tags-title {
        font-size: 1rem;
        font-weight: 500;
        color: #fff;
        margin-bottom: 7px;
    }

    .tags-list {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;

        .tag-item {
            padding: 5px 10px;
            background: #3a3d4a;
            border-radius: 6px;
            font-size: 0.8125rem;
            color: #fff;
            white-space: nowrap;
            font-weight: 400;
            cursor: pointer;

            &:hover {
                color: #99f768;
            }

            &.style-tag {
                cursor: default;

                &:hover {
                    color: #fff;
                }
            }
        }
    }
}

// ==================== 音乐类型样式（全新布局）====================
.music-detail-layout {
    width: 100%;
    height: 100%;
    min-height: 0;
    display: flex;
    background: transparent;
}

// 左侧播放区域
.music-left-panel {
    flex: 0 0 45%;
    height: 100%;
    min-height: 0;
    border-radius: 20px;
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

// 背景模糊
.music-bg-blur {
    position: absolute;
    inset: 0;
    background-size: cover;
    background-position: center;
    z-index: 0;

    &::after {
        content: '';
        position: absolute;
        inset: 0;
        background: rgba(0, 0, 0, 0.6);
    }
}

// 歌词面板容器
.lyrics-panel {
    position: absolute;
    top: 48px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 1;
    width: 90%;
    max-width: 380px;
    bottom: 160px; // 给底部控制区域留更多空间
    display: flex;
    flex-direction: column;
    align-items: center;
    overflow: hidden;
}

// 歌曲名称
.lyrics-song-title {
    font-size: 32px;
    font-weight: bold;
    color: #fff;
    margin: 0 0 12px 0;
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
    flex-shrink: 0;
}

// 复制歌词按钮
.copy-lyrics-btn {
    height: 27px;
    padding: 0 12px;
    background: rgba(255, 255, 255, 0.1);
    border: none;
    border-radius: 10px;
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    color: rgba(255, 255, 255, 0.7);
    font-size: 12px;
    transition: all 0.2s;
    margin-bottom: 16px;
    flex-shrink: 0;

    &:hover {
        background: rgba(255, 255, 255, 0.15);
        color: #fff;
    }
}

// 歌词滚动区域
.lyrics-display-area {
    flex: 1;
    width: 100%;
    min-height: 0; // 关键：让 flex 子元素可以收缩
    overflow-y: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
        display: none;
    }
}

.lyrics-display-text {
    font-size: 13px;
    line-height: 2.2;
    color: rgba(255, 255, 255, 0.8);
    white-space: pre-wrap;
    word-break: break-word;
    text-align: center;
    padding: 10px 0;
}

// 骨架屏样式 - 优化 LCP：提前渲染占位符
.lyrics-skeleton {
    padding: 10px 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
}

.lyrics-skeleton-line {
    height: 16px;
    background: linear-gradient(90deg,
            rgba(255, 255, 255, 0.1) 0%,
            rgba(255, 255, 255, 0.2) 50%,
            rgba(255, 255, 255, 0.1) 100%);
    background-size: 200% 100%;
    border-radius: 4px;
    animation: skeleton-loading 1.5s ease-in-out infinite;

    &:nth-child(1),
    &:nth-child(3),
    &:nth-child(5),
    &:nth-child(7) {
        width: 85%;
    }

    &:nth-child(2),
    &:nth-child(4),
    &:nth-child(6),
    &:nth-child(8) {
        width: 70%;
    }
}

.lyrics-skeleton-title {
    display: inline-block;
    width: 200px;
    height: 32px;
    background: linear-gradient(90deg,
            rgba(255, 255, 255, 0.1) 0%,
            rgba(255, 255, 255, 0.2) 50%,
            rgba(255, 255, 255, 0.1) 100%);
    background-size: 200% 100%;
    border-radius: 4px;
    animation: skeleton-loading 1.5s ease-in-out infinite;
}

@keyframes skeleton-loading {
    0% {
        background-position: 200% 0;
    }

    100% {
        background-position: -200% 0;
    }
}

// 黑胶容器
.vinyl-wrapper {
    position: relative;
    z-index: 1;
    width: 60%;
    max-width: 320px;
    aspect-ratio: 1 / 1;
    margin-bottom: 60px;
}

// 黑胶唱片
.vinyl-record {
    width: 100%;
    height: 100%;
    position: absolute;
    left: 0;
    top: 0;

    &.spinning {
        .vinyl-outer {
            animation: spin 3s linear infinite;
        }

        .vinyl-inner {
            animation: spin-center 3s linear infinite;
        }
    }
}

.vinyl-outer {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: rgba(17, 17, 17, 1);
    box-shadow:
        0 0 0 2px rgba(0, 0, 255, 0.1),
        0 10px 40px rgba(0, 0, 0, 0.5);
}

.vinyl-inner {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 50%;
    height: 50%;
    border-radius: 50%;
    overflow: hidden;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
}

.vinyl-cover {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.vinyl-cover-placeholder {
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, #3a3a3a 0%, #1a1a1a 100%);
    display: flex;
    align-items: center;
    justify-content: center;
}

// 唱针
.music-needle {
    position: absolute;
    width: 28%;
    max-width: 90px;
    aspect-ratio: 1 / 2;
    right: -10%;
    top: -12%;
    transform: rotate(0deg);
    transform-origin: 75% 15%;
    transition: transform 0.3s ease;
    z-index: 2;

    &.playing {
        transform: rotate(25deg);
    }
}

// 底部控制区域
.music-control-panel {
    position: absolute;
    bottom: 30px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 1;
    width: 85%;
    max-width: 380px;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(10px);
    border-radius: 24px;
    padding: 16px 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

// 进度条行
.progress-row {
    display: flex;
    align-items: center;
    gap: 12px;

    .time-text {
        font-size: 12px;
        color: rgba(255, 255, 255, 0.6);
        min-width: 36px;
        text-align: center;
    }

    .progress-bar {
        flex: 1;
        height: 4px;
        background: rgba(255, 255, 255, 0.2);
        border-radius: 2px;
        cursor: pointer;
        position: relative;

        &:hover {
            height: 6px;

            .progress-thumb {
                opacity: 1;
            }
        }

        .progress-fill {
            position: absolute;
            left: 0;
            top: 0;
            height: 100%;
            background: linear-gradient(90deg, #99f768, #7ed957);
            border-radius: 2px;
            transition: width 0.1s linear;
        }

        .progress-thumb {
            position: absolute;
            top: 50%;
            transform: translate(-50%, -50%);
            width: 12px;
            height: 12px;
            background: #99f768;
            border-radius: 50%;
            opacity: 0;
            transition: opacity 0.2s;
        }
    }
}

/* =========================
   移动端适配（弹窗全屏 + 纵向排布）
   ========================= */
@media (max-width: 768px) {
    .outside-mask-container {
        /* 按需求：手机端弹窗整体尺寸 90vw × 80vh，并居中 */
        top: 50%;
        left: 50%;
        right: auto;
        bottom: auto;
        transform: translate(-50%, -50%);
        width: 90vw;
        height: 90vh;
        padding: 0;
        background: transparent;
        overflow: hidden;
    }

    /* 继续保留全屏遮罩（不影响点击空白关闭） */
    .outside-mask-container::before {
        content: '';
        position: fixed;
        inset: 0;
        background-color: rgba(0, 0, 0, 0.7);
        z-index: -1;
    }

    .mask {
        /* 填满 90vw × 80vh 容器 */
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        width: 100%;
        height: 100%;
        padding: 12px;
        border-radius: 16px;
        overflow: hidden;
    }

    .mask {
        .close-btn {
            right: 10px;
            top: 10px;
            width: 44px;
            height: 44px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .detail-info-model {
            flex-direction: column;
            row-gap: 12px;
            height: 100%;
            padding-top: 44px;
            /* 给 close 按钮留空间 */
        }

        .detail-info-model {
            .show-plate {
                flex: none;
                height: 42vh;
                min-height: 220px;
                border-radius: 12px;

                img,
                video {
                    position: absolute;
                    inset: 0;
                    width: 100%;
                    height: 100%;
                    transform: none;
                    left: 0;
                    top: 0;
                    object-fit: contain;
                }
            }
        }
    }

    .detail-info {
        width: 100%;
        flex: 1;
        min-height: 0;
    }

    .detail-info main {
        padding: 10px 0 10px 0;
        margin-top: 10px;
    }

    .detail-info footer {
        margin-top: 10px;
    }

    .detail-info footer {
        .bottom-operation {
            flex-direction: column;
            align-items: stretch;
            gap: 10px;
        }

        .left-operate {
            width: 100%;
            justify-content: flex-start;
            gap: 8px;
            flex-wrap: wrap;
        }

        .right-create {
            width: 100%;
            text-align: center;
            padding: 12px 18px;
        }
    }

    /* 移动端没有 hover：默认靠点击 .open 展示菜单 */
    .download-wrapper:hover {
        .download-menu-native {
            opacity: 0;
            visibility: hidden;
            pointer-events: none;
        }
    }
}

// 控制按钮组
.control-buttons {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 30px;
}

.seek-btn {
    width: 24px;
    height: 24px;
    background: transparent;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
    transition: all 0.2s;

    .seek-icon {
        width: 24px;
        height: 24px;
        object-fit: contain;
    }

    &:hover {
        opacity: 0.8;
    }
}

.play-btn-large {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: #fff;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #000;
    transition: all 0.2s;

    &:hover {
        transform: scale(1.05);
    }
}

// 右侧信息区域
.music-right-panel {
    flex: 0 0 55%;
    height: 100%;
    min-height: 0;
    padding: 0 24px 20px 34px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

// 标题区
.music-header {
    flex-shrink: 0;
    margin-bottom: 10px;

    .song-label {
        font-size: 11px;
        color: rgba(255, 255, 255, 0.7); // 提高对比度：从 0.4 提升到 0.7
        letter-spacing: 1px;
        margin-bottom: 6px;
    }

    .song-title-single {
        font-size: 24px;
        font-weight: 600;
        color: #fff;
        margin: 0 0 10px 0;
        line-height: 1.2;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .user-info {
        display: flex;
        align-items: center;
        gap: 8px;

        .user-avatar {
            width: 24px;
            height: 24px;
            border-radius: 50%;
            object-fit: cover;
        }

        .user-name {
            font-size: 12px;
            color: rgba(255, 255, 255, 0.6);
        }
    }
}

// 内容滚动区域（提示词 + 歌词 + 参考音频）
.music-content-scroll {
    flex: 1 1 0;
    overflow-y: scroll;
    overflow-x: hidden;
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding-right: 8px;

    // 自定义滚动条样式
    &::-webkit-scrollbar {
        width: 4px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.2);
        border-radius: 2px;

        &:hover {
            background: rgba(255, 255, 255, 0.3);
        }
    }
}

// 提示词/歌词区块
.info-section-fixed {
    .section-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 6px;

        .section-label {
            font-size: 11px;
            color: rgba(255, 255, 255, 0.7); // 提高对比度：从 0.4 提升到 0.7
            letter-spacing: 1px;
        }
    }

    .section-content-scroll {
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 12px;
        padding: 10px;
        font-size: 13px;
        color: rgba(255, 255, 255, 0.8);
        line-height: 1.5;
        white-space: pre-wrap;
        word-break: break-word;
    }
}

.copy-icon-btn {
    background: transparent;
    border: none;
    color: rgba(255, 255, 255, 0.4);
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: all 0.2s;

    &:hover {
        background: rgba(255, 255, 255, 0.1);
        color: #fff;
    }
}

// 参考音频卡片样式
.ref-audio-card {
    display: flex;
    align-items: center;
    gap: 12px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    padding: 10px 14px;
}

.ref-audio-play-btn {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #fff;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #000;
    flex-shrink: 0;
    transition: all 0.2s;

    &:hover {
        transform: scale(1.05);
    }
}

.ref-audio-name {
    flex: 1;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.8);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.ref-audio-download-btn {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: transparent;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: rgba(255, 255, 255, 0.5);
    flex-shrink: 0;
    transition: all 0.2s;

    &:hover {
        background: rgba(255, 255, 255, 0.1);
        color: #fff;
    }
}

// 底部操作栏
.music-footer {
    display: flex;
    align-items: center;
    gap: 12px;
    padding-top: 12px;
    margin-top: 10px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    height: 56px;
    flex-shrink: 0;
}

.footer-btn {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.1);
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: rgba(255, 255, 255, 0.7);
    transition: all 0.2s;
    border: 1px solid rgba(255, 255, 255, 0.1);

    &:hover {
        background: rgba(255, 255, 255, 0.15);
        color: #fff;
    }

    &.like-btn {
        gap: 6px;
        width: auto;
        padding: 0 16px;

        span {
            font-size: 13px;
        }

        &.liked {
            color: #ff5353;
        }
    }
}

.make-similar-btn {
    flex: 1;
    height: 44px;
    border-radius: 10px;
    background: #99f768;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    cursor: pointer;
    color: #000;
    font-size: 14px;
    font-weight: 500;
    transition: all 0.2s;

    .make-similar-icon {
        width: 14px;
        height: 14px;
        object-fit: contain;
        vertical-align: middle;
    }

    span {
        line-height: 1;
    }

    &:hover {
        background: #a8ff70;
    }
}

// 旋转动画
@keyframes spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}

@keyframes spin-center {
    from {
        transform: translate(-50%, -50%) rotate(0deg);
    }

    to {
        transform: translate(-50%, -50%) rotate(360deg);
    }
}

// ==================== 音乐类型移动端适配 ====================
@media (max-width: 768px) {

    // 音乐弹窗容器
    .mask.music-mask {
        position: relative;
        width: 100%;
        max-width: 100%;
        height: 100%;
        max-height: 100%;
        padding: 12px;
        border-radius: 16px;
        overflow: hidden;
    }

    // 音乐详情布局 - 改为上下排布
    .music-detail-layout {
        flex-direction: column;
        height: 100%;
        overflow: hidden;
    }

    // 左侧播放区域（移动端变为上方）
    .music-left-panel {
        flex: 0 0 auto;
        height: 55%;
        min-height: 300px;
        max-height: 400px;
        border-radius: 16px;
    }

    // 歌词面板
    .lyrics-panel {
        top: 20px;
        bottom: 100px;
        width: 92%;
        max-width: none;
    }

    .lyrics-song-title {
        font-size: 20px;
        margin-bottom: 8px;
    }

    .copy-lyrics-btn {
        height: 24px;
        padding: 0 10px;
        font-size: 11px;
        margin-bottom: 10px;
    }

    .lyrics-display-text {
        font-size: 12px;
        line-height: 2;
    }

    // 黑胶容器
    .vinyl-wrapper {
        width: 45%;
        max-width: 160px;
        margin-bottom: 20px;
    }

    .music-needle {
        width: 24%;
        max-width: 50px;
    }

    // 底部控制区域
    .music-control-panel {
        bottom: 12px;
        width: 92%;
        max-width: none;
        padding: 12px 16px;
        border-radius: 16px;
        gap: 12px;
    }

    .progress-row {
        gap: 8px;

        .time-text {
            font-size: 11px;
            min-width: 32px;
        }
    }

    .control-buttons {
        gap: 20px;
    }

    .play-btn-large {
        width: 48px;
        height: 48px;
    }

    .seek-btn {
        width: 20px;
        height: 20px;

        .seek-icon {
            width: 20px;
            height: 20px;
        }
    }

    // 右侧信息区域（移动端变为下方）
    .music-right-panel {
        flex: 1 1 0;
        min-height: 0;
        padding: 12px 0 0 0;
        overflow: hidden;
    }

    .music-header {
        margin-bottom: 8px;
        padding: 0 4px;

        .song-label {
            font-size: 10px;
            margin-bottom: 4px;
        }

        .song-title-single {
            font-size: 18px;
            margin-bottom: 6px;
        }

        .user-info {
            gap: 6px;

            .user-avatar {
                width: 20px;
                height: 20px;
            }

            .user-name {
                font-size: 11px;
            }
        }
    }

    // 内容滚动区域
    .music-content-scroll {
        gap: 12px;
        padding-right: 4px;
    }

    .info-section-fixed {
        .section-header {
            margin-bottom: 4px;

            .section-label {
                font-size: 10px;
            }
        }

        .section-content-scroll {
            padding: 8px;
            font-size: 12px;
            border-radius: 10px;
        }
    }

    // 参考音频卡片
    .ref-audio-card {
        gap: 10px;
        padding: 8px 12px;
        border-radius: 10px;
    }

    .ref-audio-play-btn {
        width: 32px;
        height: 32px;
    }

    .ref-audio-name {
        font-size: 12px;
    }

    .ref-audio-download-btn {
        width: 28px;
        height: 28px;
    }

    // 底部操作栏
    .music-footer {
        gap: 8px;
        padding-top: 8px;
        margin-top: 8px;
        height: 48px;
        flex-wrap: wrap;
    }

    .footer-btn {
        width: 36px;
        height: 36px;
        border-radius: 8px;

        &.like-btn {
            padding: 0 10px;

            span {
                font-size: 12px;
            }
        }
    }

    .make-similar-btn {
        flex: 1;
        min-width: 100px;
        height: 36px;
        border-radius: 8px;
        font-size: 13px;

        .make-similar-icon {
            width: 12px;
            height: 12px;
        }
    }
}

// 超小屏幕适配（宽度 < 400px）
@media (max-width: 400px) {
    .music-left-panel {
        height: 50%;
        min-height: 260px;
        max-height: 340px;
    }

    .vinyl-wrapper {
        width: 40%;
        max-width: 120px;
        margin-bottom: 15px;
    }

    .music-control-panel {
        padding: 10px 12px;
        gap: 10px;
    }

    .play-btn-large {
        width: 44px;
        height: 44px;
    }

    .lyrics-panel {
        top: 16px;
        bottom: 90px;
    }

    .lyrics-song-title {
        font-size: 18px;
    }

    .music-header {
        .song-title-single {
            font-size: 16px;
        }
    }

    .music-footer {
        gap: 6px;
    }

    .footer-btn {
        width: 32px;
        height: 32px;

        svg {
            width: 16px;
            height: 16px;
        }

        &.like-btn {
            padding: 0 8px;
        }
    }

    .make-similar-btn {
        height: 32px;
        font-size: 12px;
    }
}
</style>
